import { BaseViewModel } from "../../../../core/services/BaseViewModel";
import { action, makeObservable, observable, runInAction } from "mobx";
import { Service } from "typedi";
import { IOraService } from "../OraService";
import { AppForRetirementModel, BeneficiaryBenefitStateModel, BeneficiaryBurialStateModel, BeneficiaryModel, Steps, StepStatuses, SubmissionModel } from "./ApplyModels";
import { ListItem, ServerResponse } from "../../../../core/services/BaseService";
import Helper, { toLocalDate, toServerDate } from "../../../../core/services/Helper";
import { DocumentsModel } from "../../../../core/components/UploadUtility";
import { StepStatus } from "./SidebarFormNavigation";
import { Appl } from "../../../../core/Appl";
import { AccountType, BeneficiaryPaymentOption, BeneficiaryRelation, PersonalStatus, SubmissionStatus, SupportDocs } from "../OraEnums";
import { ILovService } from "../../../utility/lov/LovService";

@Service() // eslint-disable-next-line
export default class ApplyViewModel extends BaseViewModel {

    @observable public memberId?: string;
    @observable public submissionId?: number;
    @observable public submission?: SubmissionModel = {}
    @observable public stepStatus: StepStatuses = {
        gettingStarted: StepStatus.NotStarted,
        selectDate: StepStatus.NotStarted,
        yourInformation: StepStatus.NotStarted,
        personalInformation: StepStatus.NotStarted,
        addressInformation: StepStatus.NotStarted,
        contactInformation: StepStatus.NotStarted,
        personalStatus: StepStatus.NotStarted,
        optionSelection: StepStatus.NotStarted,
        optionSelectionSelectYourOption: StepStatus.NotStarted,
        optionSelectionMemberDeclaration: StepStatus.NotStarted,
        beneficiaryDesignation: StepStatus.NotStarted,
        understandingBeneficiaries: StepStatus.NotStarted,
        beneficiaryBurialAllowance: StepStatus.NotStarted,
        taxWithholding: StepStatus.NotStarted,
        federalTaxWithholding: StepStatus.NotStarted,
        stateTaxWithholding: StepStatus.NotStarted,
        declarationTaxWithholding: StepStatus.NotStarted,
        directDeposit: StepStatus.NotStarted,
        directDepositAuthorization: StepStatus.NotStarted,
        directDepositMemberDeclaration: StepStatus.NotStarted,
        temporaryAnnuity: StepStatus.NotStarted,
        serviceRetirementSignOff: StepStatus.NotStarted,
        reviewApplication: StepStatus.NotStarted,
        uploadDocuments: StepStatus.NotStarted,
        submitApplication: StepStatus.NotStarted,
    }

    @observable public model: AppForRetirementModel = {
        submissionId: 0,
        submissionStatusId: SubmissionStatus.Draft,
        personalInformation: { submissionId: 0, validateAddress: false, validateAddressType: '', mailingCountry: 'United States', homeCountry: 'United States' },
        optionSelection: { submissionId: 0, beneficiaryPaymentOptionDeclaration: false, beneficiaryPaymentOptionId: 0 },
        temporaryAnnuity: { submissionId: 0 },
        beneficiaryDesignation: { afrDecleration: "" },
        taxWithHolding: {
            federalTaxWithHolding: { federalTaxWithhold: undefined },
            stateTaxWithHolding: { stateTaxWithhold: undefined },
            taxwithholdDecleration: false
        },
        directDeposit: { directDepositConsent: false },
        signOffModels: [],
        uploadedDocuments: []
    };
    @observable public modelLoaded = false;
    @observable public retirementDateConfirmed = false;
    @observable public homeAddressEditable = false;
    @observable public addressValidationMessage = "";
    @observable public mailingAddressEditable = false;
    @observable public contactInfoEditable = true;
    @observable public personalInfoEditable = true;
    @observable public alternativeNumberEditable = false;
    @observable public beneficiaryCurrentState?: BeneficiaryBenefitStateModel;
    @observable public beneficiaryModel: BeneficiaryModel;
    @observable public beneficiaryBurialCurrentState?: BeneficiaryBurialStateModel;
    @observable public burialBeneficiaryModel: BeneficiaryModel;
    @observable public taxWithHoldingModelCompleted = false;
    @observable public directDepositModelCompleted = false;
    @observable public accruedSickLeaveModelCompleted = false;
    @observable public screaMembershipModelCompleted = false;
    @observable public signOffCheckedItemsCount = 0;
    @observable public documentUploadCompleted = false;
    @observable public documentUploading = false;
    @observable public activeDocument: DocumentsModel = { docTypeId: 0 };
    @observable public uploadDialogVisible: boolean = false;
    @observable public previousLovPersonalStatusId: any;

    @observable public addressListItems?: Array<ListItem> = [];

    @observable public afrFile?: any
    @observable public navigatedFromReviewScreen: boolean = false;
    @observable requiredDocuments: DocumentsModel[] = [];

    @observable public docuSignEventCode?: string = '';
    @observable public docuSignMessageTitle?: string = '';
    @observable public docuSignMessage?: string = '';
    @observable public submitApplicationValidationMessageTitile?: string = '';
    @observable public submitApplicationValidationMessage?: string = '';

    private _service: IOraService;
    private lovService: ILovService;

    constructor() {
        super();
        this._service = Appl.Services.get<IOraService>('IOraService');
        this.lovService = Appl.Services.get<ILovService>('ILovService');
        this.beneficiaryModel = {};
        this.beneficiaryCurrentState = {
            isMarriedOrRegisteredPartnership: false,
            manuallyProvidePercentage: false,
            addNewTopButtonVisible: false,
            addNewSideButtonVisible: false,
            showDesignateMinorChildOptions: false,
            readOnlyRelationship: false,
            readMoreVisible: false, validationMessageTitle: "Attention", validationMessage: "", isAddingBeneficiary: false,
            isEditingBeneficiary: false, memberDeclarationVisible: false, selectedBeneficiary: 0
        };
        this.beneficiaryBurialCurrentState = {
            selectedBeneficiary: 0,
            validationMessageTitle: "Attention",
            addNewTopButtonVisible: false, addNewSideButtonVisible: false,
            validationMessage: "", isAddingBeneficiary: false,
            isEditingBeneficiary: false,
        }
        this.burialBeneficiaryModel = {};
        makeObservable(this);
    }

    @action public async validateReferrerUrl(): Promise<boolean> {
        let referrer = await Appl.Cache.getSessionAsync<string>("Referrer");
        console.log(`Referrer: ${referrer}::${Appl.Setting.MyScersPortalUrl}`)
        if (Appl.Setting.EnvironmentName !== 'local') {
            if (!referrer) {
                this.navigate('/noauthorization');
                return false;
            } else {
                if (!referrer?.toLowerCase().includes(Appl.Setting.MyScersPortalUrl?.toLowerCase()!)) {
                    this.navigate('/noauthorization');
                    return false;
                }
            }
        }
        return true;
    }

    @action public async validateAndVerifyUrl(url: string): Promise<boolean> {
        let referrer = document.referrer.toLowerCase();
        console.log(`Referrer: ${referrer}::${Appl.Setting.MyScersPortalUrl}`)
        // alert(`Referrer: ${referrer}::${Appl.Setting.MyScersPortalUrl}`);
        if (Appl.Setting.EnvironmentName !== 'local') {
            let storedReferrer = await Appl.Cache.getSessionAsync<string>("Referrer");
            if (!storedReferrer) {
                if (referrer.includes(Appl.Setting.MyScersPortalUrl?.toLowerCase()!)) {
                    await Appl.Cache.addOrUpdateSessionAsync("Referrer", referrer);
                }
            }
            if (!referrer.includes(Appl.Setting.MyScersPortalUrl?.toLowerCase()!)) {
                this.navigate('/noauthorization');
                return false;
            }
        }

        let result = await this._service?.validateAndVerifyUrl({ url });
        if (result.success) {
            runInAction(async () => {
                this.memberId = result.data.memberId;
                if (result.data.isNewUser === true) {
                    this.navigate(`/ora/application/${this.memberId}/${Steps.GettingStarted}`);
                } else {
                    if (result.data.isSubmitted === true) {
                        this.navigate(`/ora/application/${this.memberId}/submit-application`);
                    } else {
                        this.navigate(`/ora/application/${this.memberId}`);
                    }
                }
                return true;
            });
        } else {
            Appl.Error.handle(result.error, true);
            return false;
        }
        return false;
    }

    @action public async getSubmissionIdAsync(): Promise<number> {
        const submissionId = parseInt(Appl.Cache.get("submission_id")!);
        return submissionId;
    }

    @action public async getStepStatuses(memberId: string): Promise<void> {
        this.memberId = memberId;
        let result = await this._service?.getSubmissionStepStatus(memberId);
        if (result.success) {
            runInAction(async () => {
                this.stepStatus = result.data;
            });
        } else {
            let error = Appl.Error.getValidationError(result.error);
            if (error.pointer === "no-submission") {
                this.navigate(`/ora/application/${memberId}/${Steps.GettingStarted}`)
            } else {
                Appl.MessageBox.show(error.detail!, 'Error', '', 'Okay', true);
            }
        }
    }

    @action public async getSubmissionByMemberIdAsync(memberId: string): Promise<SubmissionModel> {
        let result = await this._service?.getSubmissionByMemberIdAsync(memberId);
        if (result.success) {
            runInAction(async () => {
                this.submission = result.data;
                this.submissionId = this.submission?.submissionId;
            }
            );
        } else {

            let error = Appl.Error.getValidationError(result.error);
            // alert(error.pointer);
            if (error.pointer !== "no-submission") {
                Appl.Error.handle(result.error, true);
            }
        }
        return this.submission!;
    }

    @action public async loadStepAsync(step: Steps, memberId: string): Promise<void> {
        this.memberId = memberId;
        let result: ServerResponse;
        await Appl.Cache.removeAsync("submission_id");
        let refererIsValid = await this.validateReferrerUrl();
        if (!refererIsValid) {
            return;
        }
        Appl.Validator.init();
        this.modelLoaded = false;
        result = await this._service?.getSubmissionByMemberIdAsync(memberId);
        if (result.success) {
            runInAction(async () => {
                this.submission = result.data;
                this.submissionId = this.submission?.submissionId;
                if (this.submission?.lovSubmissionStatusId && this.submission.lovSubmissionStatusId !== SubmissionStatus.Draft) {
                    await this.navigateToStepAsync(`${Steps.SubmitApplication}`)
                }

                await Appl.Cache.addOrUpdateAsync("submission_id", this.submissionId!.toString());

                this.submissionId = await this.getSubmissionIdAsync();
                if (!this.modelLoaded) {
                    await this.getStepStatuses(memberId);
                    result = await this._service?.getAppForRetirementBySubmissionIdAsync(this.submissionId);
                    if (result.success) {
                        runInAction(() => {
                            this.model = result.data;
                            if (this.model) {
                                this.model.personalInformation.confirmEmail = this.model.personalInformation.email;
                                this.model.personalInformation.fullName = `${this.model.personalInformation.firstName} ${this.model.personalInformation.middleName ? this.model.personalInformation.middleName + ' ' : ''}${this.model.personalInformation.lastName}`;
                                this.setPersonalInfoToLocalDates();
                                if (!this.model.personalInformation.homeAddress) {
                                    this.homeAddressEditable = true;
                                }
                                if (this.model.personalInformation.alternatePhone) {
                                    this.alternativeNumberEditable = true;
                                }
                                this.previousLovPersonalStatusId = this.model.personalInformation.lovPersonalStatusId;
                                //Beneficiary Designation
                                //Hide the Add/Edit Controls if you are clicking on Back Button
                                this.cancelAddBeneficiaryAsync();
                                if (this.model.beneficiaryDesignation.beneficiaries) {
                                    this.beneficiaryCurrentState!.selectedBeneficiary = 0;
                                    this.beneficiaryModel = this.model.beneficiaryDesignation.beneficiaries[this.beneficiaryCurrentState!.selectedBeneficiary];
                                    if (this.beneficiaryModel?.beneBirthDate) {
                                        this.beneficiaryModel.beneBirthDate = toLocalDate(this.beneficiaryModel.beneBirthDate);
                                    }
                                }
                                if (this.model.beneficiaryDesignation.burialBeneficiaries) {
                                    this.beneficiaryBurialCurrentState!.selectedBeneficiary = 0;
                                    this.burialBeneficiaryModel = this.model.beneficiaryDesignation.burialBeneficiaries[this.beneficiaryBurialCurrentState!.selectedBeneficiary];
                                    if (this.burialBeneficiaryModel?.beneBirthDate) {
                                        this.burialBeneficiaryModel.beneBirthDate = toLocalDate(this.burialBeneficiaryModel.beneBirthDate);
                                    }
                                }
                                this.beneficiaryBurialCurrentState!.summaryVisible = false;
                                this.showHideBurialBeneficiaryAddNewButton();
                                this.setRequiredDocuments();
                                //Sign Off Model
                                this.signOffCheckedItemsCount = this.model.signOffModels.filter(o => o.active)?.length;
                            }

                            this.modelLoaded = true;
                        })
                    } else {
                        Appl.Error.handle(result.error, true);
                        return;
                    }
                }
                await this.setStepStatus(step, StepStatus.Active);
            })
        } else {
            let error = Appl.Error.getValidationError(result.error);
            if (error.pointer !== "no-submission") {
                Appl.Error.handle(result.error, true);
            } else {
                this.homeAddressEditable = true;
                this.navigate('/noauthorization')
            }
            return;
        }
    }

    private setPersonalInfoToLocalDates(): void {
        this.model.personalInformation.retirementEffDate = toLocalDate(this.model.personalInformation.retirementEffDate);
        this.model.personalInformation.birthDate = toLocalDate(this.model.personalInformation.birthDate);
        this.model.personalInformation.marriageDate = toLocalDate(this.model.personalInformation.marriageDate);
        this.model.personalInformation.partnershipDate = toLocalDate(this.model.personalInformation.partnershipDate);
    }

    private setPersonalInfoToServerDates(): void {
        if (this.model.personalInformation.retirementEffDate) {
            this.model.personalInformation.retirementEffDate = toServerDate(this.model.personalInformation.retirementEffDate);
        }
        if (this.model.personalInformation.birthDate) {
            this.model.personalInformation.birthDate = toServerDate(this.model.personalInformation.birthDate);
        }
        if (this.model.personalInformation.marriageDate) {
            this.model.personalInformation.marriageDate = toServerDate(this.model.personalInformation.marriageDate);
        }
        if (this.model.personalInformation.partnershipDate) {
            this.model.personalInformation.partnershipDate = toServerDate(this.model.personalInformation.partnershipDate);
        }
    }

    private async setStepStatus(step: Steps, status: StepStatus): Promise<void> {
        if (status === StepStatus.Active) {
            this.setBeneficiaryBenefitCurrentState();
            if (this.beneficiaryBurialCurrentState) {
                this.beneficiaryBurialCurrentState!.relationships = this.model.beneficiaryRelationListItems?.filter(o => (o.key === BeneficiaryRelation.EntityTrustOrg || o.key === BeneficiaryRelation.Person));
            }
            return;
        }
        switch (step) {
            case Steps.GettingStarted: {
                this.stepStatus.gettingStarted = status;
                break;
            }
            case Steps.SelectDate: {
                this.stepStatus.selectDate = status;
                this.retirementDateConfirmed = false;
                break;
            }
            case Steps.YourInformation: {
                this.stepStatus.yourInformation = status;
                break;
            }
            case Steps.PersonalInformation: {
                this.personalInfoEditable = false;
                this.stepStatus.yourInformation = StepStatus.InProgress;
                this.stepStatus.personalInformation = status;
                break;
            }
            case Steps.AddressInformation: {
                this.homeAddressEditable = false;
                this.mailingAddressEditable = false;
                this.stepStatus.yourInformation = StepStatus.InProgress;
                this.stepStatus.addressInformation = status;
                break;
            }
            case Steps.ContactInformation: {
                this.stepStatus.yourInformation = StepStatus.InProgress;
                this.stepStatus.contactInformation = status;
                break;
            }
            case Steps.PersonalStatus: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.yourInformation = StepStatus.Completed;
                } else {
                    this.stepStatus.yourInformation = StepStatus.InProgress;
                }
                this.stepStatus.personalStatus = status;

                break;
            }
            case Steps.OptionSelection: {
                this.stepStatus.optionSelection = StepStatus.InProgress;
                break;
            }
            case Steps.OptionSelectionSelectYourOption: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.optionSelection = StepStatus.Completed;
                } else {
                    this.stepStatus.optionSelection = StepStatus.InProgress;
                }
                this.stepStatus.optionSelectionSelectYourOption = status;
                break;
            }
            case Steps.OptionSelectionMemberDeclaration: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.optionSelection = StepStatus.Completed;
                } else {
                    this.stepStatus.optionSelection = StepStatus.InProgress;
                }
                this.stepStatus.optionSelectionMemberDeclaration = status;
                break;
            }
            case Steps.BeneficiaryDesignation: {
                this.beneficiaryCurrentState!.completed = false;
                this.stepStatus.beneficiaryDesignation = StepStatus.InProgress;
                break;
            }
            case Steps.UnderstandingBeneficiaries: {
                this.beneficiaryCurrentState!.completed = false;
                if (status === StepStatus.Completed) {
                    this.stepStatus.beneficiaryDesignation = StepStatus.Completed;
                } else {
                    this.stepStatus.beneficiaryDesignation = StepStatus.InProgress;
                }
                this.stepStatus.understandingBeneficiaries = status;
                break;
            }
            case Steps.BeneficiaryBenefit: {
                this.beneficiaryCurrentState!.completed = false;
                if (status === StepStatus.Completed) {
                    this.stepStatus.beneficiaryDesignation = StepStatus.Completed;
                } else {
                    this.stepStatus.beneficiaryDesignation = StepStatus.InProgress;
                }
                this.stepStatus.beneficiaryBenefit = status;
                break;
            }
            case Steps.BeneficiaryBurialAllowance: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.beneficiaryDesignation = StepStatus.Completed;
                } else {
                    this.stepStatus.beneficiaryDesignation = StepStatus.InProgress;
                }
                this.stepStatus.beneficiaryBurialAllowance = status;
                this.beneficiaryCurrentState!.completed = false;
                break;
            }
            case Steps.TaxWithholding: {
                this.stepStatus.taxWithholding = StepStatus.InProgress;
                break;
            }
            case Steps.FederalTaxWithholding: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.taxWithholding = StepStatus.Completed;
                } else {
                    this.stepStatus.taxWithholding = StepStatus.InProgress;
                }
                this.stepStatus.federalTaxWithholding = status;
                break;
            }
            case Steps.StateTaxWithholding: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.taxWithholding = StepStatus.Completed;
                } else {
                    this.stepStatus.taxWithholding = StepStatus.InProgress;
                }
                this.stepStatus.stateTaxWithholding = status;
                break;
            }
            case Steps.DeclarationTaxWithholding: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.taxWithholding = StepStatus.Completed;
                } else {
                    this.stepStatus.taxWithholding = StepStatus.InProgress;
                }
                this.stepStatus.declarationTaxWithholding = status;
                this.taxWithHoldingModelCompleted = false;
                break;
            }
            case Steps.DirectDeposit: {
                this.stepStatus.directDeposit = StepStatus.InProgress;
                this.directDepositModelCompleted = false;
                break;
            }
            case Steps.DirectDepositAuthorization: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.directDeposit = StepStatus.Completed;
                } else {
                    this.stepStatus.directDeposit = StepStatus.InProgress;
                }
                this.stepStatus.directDepositAuthorization = status;
                break;
            }
            case Steps.DirectDepositMemberDeclaration: {
                if (status === StepStatus.Completed) {
                    this.stepStatus.directDeposit = StepStatus.Completed;
                } else {
                    this.stepStatus.directDeposit = StepStatus.InProgress;
                }
                this.stepStatus.directDepositMemberDeclaration = status;
                break;
            }
            case Steps.TemporaryAnnuity: {
                this.stepStatus.temporaryAnnuity = status;
                break;
            }
            case Steps.ServiceRetirementSignOff: {
                this.stepStatus.serviceRetirementSignOff = status;
                break;
            }
            case Steps.ReviewApplication: {
                this.stepStatus.reviewApplication = status;
                break;
            }
            case Steps.UploadDocuments: {
                this.stepStatus.uploadDocuments = status;
                break;
            }
            case Steps.SubmitApplication: {
                this.stepStatus.submitApplication = status;
                this.model.personalInformation.fullName = `${this.model.personalInformation.firstName} ${this.model.personalInformation.middleName ? this.model.personalInformation.middleName + ' ' : ''}${this.model.personalInformation.lastName}`;
                break;
            }
            default: {
                break;
            }
        }

        await this._service?.updateSubmissionStepStatus(this.submissionId!, this.stepStatus);
    }

    @action public async onGettingStartedStepChangeAsync(): Promise<boolean> {
        await this.setStepStatus(Steps.GettingStarted, StepStatus.Completed);
        return true;
    }

    @action public async onRetirementDateChangeAsync(): Promise<boolean> {
        Appl.Validator.init();
        Appl.Validator.validateDate("Retirement Date", "retirementEffDate", this.model.personalInformation.retirementEffDate, true, 'mm/dd/yyyy');
        if (!Appl.Validator.isValid()) { return false; }
        let dateCheck = await this.doRetirementDateValidationAsync(this.model.personalInformation.retirementEffDate!, false);
        if (dateCheck !== "") {
            Appl.MessageBox.show(dateCheck, 'Retirement Date Notification', '', 'I Understand', true);
        }
        return false;
    };

    @action public async doRetirementDateValidationAsync(date: Date, pastDateOnly: boolean): Promise<string> {
       pastDateOnly = false;
        let noOfDays = await this.getDaysAsync(date);
        if (noOfDays < 0) {
            return `The retirement date you previously selected has already passed and can no longer be used. To continue your application, please choose a new retirement date.`;
        }
        if (!pastDateOnly) {
            if (noOfDays > 60) {
                return `You have selected a retirement date that is more than 60 days from today. You may continue with the Online Retirement Application; however, please be aware that you will not be able to electronically sign and submit your application until you are within 60 days of your selected retirement date.<br/><br/>Rest assured, any progress you make on your application-whether in part or in whole-will be saved. You can return at any time to complete the submission process when you become eligible to do so.`;
            }
        }

        return "";
    };

    @action public async getDaysAsync(date: Date): Promise<number> {

        let fromString = undefined;//toServerDate(new Date())?.split('T')[0];
        let toString = toServerDate(date)?.split('T')[0];
        const result = await this._service?.getDays({ fromDate: fromString, toDate: toString! });
        // const result = await this._service?.getDays({ fromDate: new Date("07/28/2025"), toDate: new Date("09/27/2025")! });

        if (result.success) {
            console.log(`FromDate: ${fromString}, To Date: ${toString}, # Days: ${result?.data}`)
            return result?.data;
        } else {
            Appl.Error.handle(result.error, true);
            return -1;
        }
    }
    @action public async onMarriageDateChangeAsync(): Promise<boolean> {
        let year = Helper.getYears(new Date(this.model.personalInformation.marriageDate!), new Date(this.model.personalInformation.retirementEffDate!));
        if (year < 1) {
            Appl.MessageBox.show(`Based on your entry, your marriage or domestic partnership began less than one year before your selected retirement date.<br/><br/>Please note that for some retirement options, such as the Unmodified Allowance, your spouse or partner must have been married to or registered with you for at least one year prior to retirement to qualify for a monthly continuance.<br/><br/>Your spouse or partner may still be eligible for a continuance under other options, depending on their relationship to you and other eligibility criteria.<br/><br/>You'll receive additional guidance when selecting your retirement option and designating your beneficiary.`, 'Spouse/Partner Eligibility May Be Limited', '', 'I Understand', true);
        }
        return false;
    };

    @action public async onPartnershipDateChangeAsync(): Promise<boolean> {
        let year = Helper.getYears(new Date(this.model.personalInformation.partnershipDate!), new Date(this.model.personalInformation.retirementEffDate!));
        if (year < 1) {
            Appl.MessageBox.show(`Based on your entry, your marriage or domestic partnership began less than one year before your selected retirement date.<br/><br/>Please note that for some retirement options, such as the Unmodified Allowance, your spouse or partner must have been married to or registered with you for at least one year prior to retirement to qualify for a monthly continuance.<br/><br/>Your spouse or partner may still be eligible for a continuance under other options, depending on their relationship to you and other eligibility criteria.<br/><br/>You'll receive additional guidance when selecting your retirement option and designating your beneficiary.`, 'Spouse/Partner Eligibility May Be Limited', '', 'I Understand', true);
        }
        return false;
    };


    @action public async onSelectDateChangeAsync(): Promise<boolean> {
        Appl.Validator.init();
        Appl.Validator.validateDate("Retirement Date", "retirementEffDate", this.model.personalInformation.retirementEffDate, true, 'mm/dd/yyyy');
        let noOfDays = await this.getDaysAsync(this.model.personalInformation.retirementEffDate!);
        if (noOfDays < 0) {
            // Appl.Validator.setError('retirementEffDate', 'The retirement date you previously selected has already passed and can no longer be used. To continue your application, please choose a new retirement date.')
            this.submitApplicationValidationMessageTitile = `Attention: Retirement Date No Longer Valid`;
            this.submitApplicationValidationMessage = `The retirement date you previously selected has already passed and can no longer be used. To continue your application, please choose a new retirement date.`;
            return false;
        }
        if (!Appl.Validator.isValid()) { return false; }
        this.submitApplicationValidationMessageTitile = "";
        this.submitApplicationValidationMessage = "";
        this.setPersonalInfoToServerDates();
        const result = await this._service?.createOrUpdateRetirementDateAsync({ submissionId: this.submissionId!, retirementDate: this.model.personalInformation.retirementEffDate!, memberId: this.memberId });
        if (result.success) {
            this.retirementDateConfirmed = true;
            this.submissionId = result.data;
            this.model.personalInformation.submissionId = result.data;
            return true;
        } else {
            await Appl.Validator.setError("retirementEffDate", result.error?.detail!);
            return false;
        }
    }

    @action public async onSelectDateStepCompleteAsync(): Promise<boolean> {
        await this.setStepStatus(Steps.SelectDate, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.YourInformation}/${Steps.PersonalInformation}`);
        return true
    }

    @action public async onPersonalInformationStepChangeAsync(): Promise<boolean> {
        Appl.Validator.init();
        await Appl.Validator.validateString('First Name', 'firstName', this.model.personalInformation.firstName, true, 100);
        await Appl.Validator.validateString('Middle Name', 'middleName', this.model.personalInformation.middleName, false, 10);
        await Appl.Validator.validateString('Last Name', 'lastName', this.model.personalInformation.lastName, true, 100);
        Appl.Validator.validateDate("Date of Birth", "birthDate", this.model.personalInformation.birthDate, true, 'mm/dd/yyyy');
        await Appl.Validator.validateNumber('SSN', 'ssn', this.model.personalInformation.ssn, true, undefined, undefined, 4, 4);
        if (!Appl.Validator.isValid()) { return false; }
        this.model.personalInformation.personalInfoVerified = 'Y';
        this.model.personalInformation.memberId = this.memberId;
        this.setPersonalInfoToServerDates();
        const result = await this._service?.createOrUpdatePersonalInfoAsync(this.model.personalInformation);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        this.personalInfoEditable = false;
        return true;
    }

    @action public async onPersonalInformationStepCompleteAsync(): Promise<boolean> {
        await this.setStepStatus(Steps.PersonalInformation, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.YourInformation}/${Steps.AddressInformation}`)
        return true;
    }


    @action
    public onAddressInfoChange(e: any, id: string) {
        (this.model.personalInformation as any)[id] = e.target?.value;
    }

    @action public async onAddressInformationStepChangeAsync(): Promise<boolean> {
        if (!(this.model.personalInformation.homeAddress)) {
            this.showError("Home Address is required.");
            return false;
        }
        await this.setStepStatus(Steps.AddressInformation, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.YourInformation}/${Steps.ContactInformation}`);
        return true;
    }

    @action public async setHomeAddressEditable(editable: boolean): Promise<void> {
        this.homeAddressEditable = editable;
        this.addressValidationMessage = "";
        if (!editable) {
            await this.navigateToStepAsync(`${Steps.YourInformation}/${Steps.AddressInformation}`)
        }
    }

    @action public async setMailingAddressEditable(editable: boolean): Promise<void> {
        this.mailingAddressEditable = editable;
        this.addressValidationMessage = "";
        if (!editable) {
            await this.navigateToStepAsync(`${Steps.YourInformation}/${Steps.AddressInformation}`)
        }
    }

    @action public async removeMailingAddress(): Promise<void> {
        this.model.personalInformation.mailingAddress = "";
        this.model.personalInformation.mailingAddress2 = "";
        this.model.personalInformation.mailingCity = "";
        this.model.personalInformation.mailingState = "";
        this.model.personalInformation.mailingZip = "";
        this.model.personalInformation.mailingCountry = "";
        this.model.personalInformation.validateAddress = true;
        this.model.personalInformation.validateAddressType = 'mail';
        this.addressValidationMessage = "";
        const result = await this._service?.createOrUpdatePersonalInfoAsync(this.model.personalInformation);
        if (!result.success) {
            let error = Appl.Error.getValidationError(result.error);
            this.addressValidationMessage = error?.detail!;
        }
    }

    @action public async onHomeAddressAddEditAsync(): Promise<boolean> {
        this.addressValidationMessage = "";
        Appl.Validator.init();
        await Appl.Validator.validateString('Home Address Line 1', 'homeAddress', this.model.personalInformation.homeAddress, true, 200);
        await Appl.Validator.validateString('Home City', 'homeCity', this.model.personalInformation.homeCity, true, 50);
        await Appl.Validator.validateString('Home State', 'homeState', this.model.personalInformation.homeState, true, 50);
        await Appl.Validator.validateString('Home Country', 'homeCountry', this.model.personalInformation.homeCountry, true, 50);
        if (this.model.personalInformation.homeCountry === 'United States') {
            await Appl.Validator.validateZip('Home Zip', 'homeZip', this.model.personalInformation.homeZip, true, 5);
        } else {
            await Appl.Validator.validateString('Home Zip', 'homeZip', this.model.personalInformation.homeZip, true, 10);
        }

        if (!Appl.Validator.isValid()) { return false; }
        this.model.personalInformation.validateAddress = true;
        this.model.personalInformation.validateAddressType = 'home';
        this.setPersonalInfoToServerDates();
        const result = await this._service?.createOrUpdatePersonalInfoAsync(this.model.personalInformation);
        if (!result.success) {
            let error = Appl.Error.getValidationError(result.error);
            this.addressValidationMessage = error?.detail!;
            return false;
        }
        this.model.personalInformation.homeAddress = result.data?.homeAddress;
        this.model.personalInformation.homeAddress2 = result.data?.homeAddress2;
        this.model.personalInformation.homeCity = result.data?.homeCity;
        this.model.personalInformation.homeState = result.data?.homeState;
        this.model.personalInformation.homeCountry = result.data?.homeCountry;
        this.model.personalInformation.homeZip = result.data?.homeZip;

        this.model.personalInformation.validateAddress = false;
        this.model.personalInformation.validateAddressType = '';
        this.homeAddressEditable = false;
        this.mailingAddressEditable = false;
        return true;
    }

    @action public async onMailingAddressAddEditAsync(): Promise<boolean> {
        this.addressValidationMessage = "";
        Appl.Validator.init();
        await Appl.Validator.validateString('Mailing Address Line 1', 'mailingAddress', this.model.personalInformation.mailingAddress, false, 200);
        await Appl.Validator.validateString('Mailing City', 'mailingCity', this.model.personalInformation.mailingCity, false, 50);
        await Appl.Validator.validateString('Mailing State', 'mailingState', this.model.personalInformation.mailingState, false, 50);
        await Appl.Validator.validateString('Mailing Country', 'mailingCountry', this.model.personalInformation.mailingCountry, false, 50);
        if (this.model.personalInformation.mailingCountry === 'United States') {
            await Appl.Validator.validateZip('Mailing Zip', 'mailingZip', this.model.personalInformation.mailingZip, false, 5);
        } else {
            await Appl.Validator.validateString('Mailing Zip', 'mailingZip', this.model.personalInformation.mailingZip, false, 10);
        }
        if (!Appl.Validator.isValid()) { return false; }
        this.model.personalInformation.validateAddress = true;
        this.model.personalInformation.validateAddressType = 'mail';
        this.setPersonalInfoToServerDates();
        const result = await this._service?.createOrUpdatePersonalInfoAsync(this.model.personalInformation);
        if (!result.success) {
            let error = Appl.Error.getValidationError(result.error);
            this.addressValidationMessage = error?.detail!;
            return false;
        }
        this.model.personalInformation.mailingAddress = result.data?.mailingAddress;
        this.model.personalInformation.mailingAddress2 = result.data?.mailingAddress2;
        this.model.personalInformation.mailingCity = result.data?.mailingCity;
        this.model.personalInformation.mailingState = result.data?.mailingState;
        this.model.personalInformation.mailingZip = result.data?.mailingZip;
        this.model.personalInformation.mailingCountry = result.data?.mailingCountry;

        this.model.personalInformation.validateAddress = false;
        this.model.personalInformation.validateAddressType = '';
        this.homeAddressEditable = false;
        this.mailingAddressEditable = false;
        return true;
    }

    @action public async setAlternativeNumberEditable(editable: boolean): Promise<void> {
        this.alternativeNumberEditable = editable;
        if (!editable) {
            this.model.personalInformation.alternatePhone = undefined;
            this.model.personalInformation.alternatePhoneType = undefined;
        }
    }

    @action public async onContactInformationStepChangeAsync(): Promise<boolean> {
        if (this.contactInfoEditable) {
            Appl.Validator.init();
            await Appl.Validator.validateEmail('Email Address', 'email', this.model.personalInformation.email, true, 100);
            await Appl.Validator.validateEmail('Confirm Email Address', 'confirmEmail', this.model.personalInformation.confirmEmail, true, 100);
            if (this.model.personalInformation.email !== this.model.personalInformation.confirmEmail) {
                Appl.Validator.setError("confirmEmail", "Emails do not match.");
            }

            await Appl.Validator.validatePhone('Primary Phone Number', 'phone', this.model.personalInformation.phone, true, 20);
            await Appl.Validator.validateString('Phone Type', 'phoneType', this.model.personalInformation.phoneType, true, 10);
            if (this.alternativeNumberEditable) {
                await Appl.Validator.validatePhone('Alternate Phone Number', 'alternatePhone', this.model.personalInformation.alternatePhone, true, 20);
                await Appl.Validator.validateString('Alternate Phone Type', 'alternatePhoneType', this.model.personalInformation.alternatePhoneType, true, 10);
            }
            if (!Appl.Validator.isValid()) { return false; }
            this.setPersonalInfoToServerDates();
            const result = await this._service?.createOrUpdatePersonalInfoAsync(this.model.personalInformation);
            if (!result.success) {
                Appl.Error.handle(result.error, true);
                return false;
            }
            this.contactInfoEditable = false;
            return true;
        } else {
            await this.setStepStatus(Steps.ContactInformation, StepStatus.Completed);
            this.contactInfoEditable = true;
            await this.navigateToStepAsync(`${Steps.YourInformation}/${Steps.PersonalStatus}`)
            return true;
        }
    }

    @action public onPersonalStatusOptionChangeAsync(e: any): void {
        this.model.personalInformation.lovPersonalStatusId = e.target.value;
        let message = '';
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Divorced) {
            message = `Because you selected Divorced, SCERS may require documents related to your former marriage to fully process your retirement application.<br/><br/>You’ll be asked to review and initial the Community Property Interest clause in the Service Retirement Sign-Off section of your application.<br/><br/>If you have relevant documents, you’ll be able to upload them during the Upload Documents step near the end of the process. SCERS may follow up to request additional information based on their review.`;
        } else if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.TerminatedDomesticPartnership) {
            message = `Because you selected Terminated Domestic Partnership, SCERS may require documents related to your former partnership to fully process your retirement application.<br/><br/>You’ll be asked to review and initial the Community Property Interest clause in the Service Retirement Sign-Off section of your application.<br/><br/>If you have relevant documents, you’ll be able to upload them during the Upload Documents step near the end of the process. SCERS may follow up to request additional information based on their review.`;
        }
        if (message !== '') {
            Appl.MessageBox.show(message, 'Alert: Community Property Documents May Be Required', '', 'I Understand', true);
        }
    }

    @action public async onPersonalStatusStepChangeAsync(): Promise<boolean> {

        if (!this.navigatedFromReviewScreen) {
            this.previousLovPersonalStatusId = this.model.personalInformation.lovPersonalStatusId;
        }
        Appl.Validator.init();
        await Appl.Validator.validateString('Personal Status', 'lovPersonalStatusId', this.model.personalInformation.lovPersonalStatusId, true, 10);
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Married) {//Married
            await Appl.Validator.validateDate('Date of Marriage', 'marriageDate', this.model.personalInformation.marriageDate, true, '');
            if (this.model.personalInformation.marriageDate && new Date(this.model.personalInformation.marriageDate!) > new Date()) {
                Appl.Validator.setError("marriageDate", "Marriage date should not be a future date.");
            }
        } else if (this.model.personalInformation.lovPersonalStatusId == PersonalStatus.RegistedDomesticPartner) {//Registered Domestic Partnership
            await Appl.Validator.validateDate('Date of Partnership', 'partnershipDate', this.model.personalInformation.partnershipDate, true, '');
            if (this.model.personalInformation.partnershipDate && new Date(this.model.personalInformation.partnershipDate!) > new Date()) {
                Appl.Validator.setError("partnershipDate", "Partnership date should not be a future date.");
            }
        }

        if (!Appl.Validator.isValid()) { return false; }
        this.setPersonalInfoToServerDates();
        const result = await this._service?.createOrUpdatePersonalInfoAsync(this.model.personalInformation);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.PersonalStatus, StepStatus.Completed);
        if (this.navigatedFromReviewScreen) {
            await this.navigateToReviewStepAsync();
        } else {
            await this.navigateToStepAsync(`${Steps.OptionSelection}/${Steps.OptionSelectionSelectYourOption}`)
        }

        return true;
    }

    @action public async onOptionSelectionSelectYourOptionStepAsync(): Promise<boolean> {
        const result = await this._service?.createOrUpdateOptionSelectionAsync(this.model.optionSelection);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.cancelAddBeneficiaryAsync();
        await this.setStepStatus(Steps.OptionSelectionSelectYourOption, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.OptionSelection}/${Steps.OptionSelectionMemberDeclaration}`)
        return true;
    }

    @action public async onOptionSelectionMemberDeclarationStepAsync(): Promise<boolean> {
        const result = await this._service?.createOrUpdateOptionSelectionAsync(this.model.optionSelection);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.OptionSelectionMemberDeclaration, StepStatus.Completed);
        if (this.navigatedFromReviewScreen) {
            await this.navigateToReviewStepAsync();
        } else {
            await this.navigateToStepAsync(`${Steps.BeneficiaryDesignation}/${Steps.UnderstandingBeneficiaries}`);
        }

        return true;
    }

    @action public async onUnderstandingBeneficiariesStepCompletedAsync(): Promise<boolean> {
        await this.setStepStatus(Steps.UnderstandingBeneficiaries, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.BeneficiaryDesignation}/${Steps.BeneficiaryBenefit}`)
        return true;
    }

    @action public async setBeneficiaryReadMoreVisible(visible: boolean): Promise<void> {
        this.beneficiaryCurrentState!.readMoreVisible! = visible;
    }

    @action async addBeneficiaryAsync(): Promise<void> {
        this.beneficiaryCurrentState!.validationMessage = "";
        this.beneficiaryCurrentState!.isAddingBeneficiary = true;
        this.beneficiaryCurrentState!.readMoreVisible = false;
        this.beneficiaryModel = { beneCountry: 'United States' };
        if (this.model.beneficiaryDesignation.beneficiaries) {
            this.model.beneficiaryDesignation.beneficiaries.push({ afrMbrAffidavitId: this.model.beneficiaryDesignation.afrMbrAffidavitId });
            this.beneficiaryCurrentState!.selectedBeneficiary = this.model.beneficiaryDesignation.beneficiaries.length - 1;
            this.beneficiaryModel = this.model.beneficiaryDesignation.beneficiaries[this.beneficiaryCurrentState!.selectedBeneficiary];
        }

        if (this.beneficiaryCurrentState?.isMarriedOrRegisteredPartnership
            && this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance
            && this.model.beneficiaryDesignation?.beneficiaries?.length! > 1
            && this.model.beneficiaryDesignation.beneficiaries?.filter(o => o.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild).length! > 0) {
            this.beneficiaryModel.lovBeneRelationshipId = BeneficiaryRelation.MinorDependentChild;
        }

        this.changeReadOnlyRelationshipStatus();


        if (!this.beneficiaryModel.beneCountry) {
            this.beneficiaryModel.beneCountry = 'United States';
        }

        this.setMemberDeclarationTextAndAddButtonVisibility();
    }

    @action changeReadOnlyRelationshipStatus(): void {
        this.beneficiaryCurrentState!.readOnlyRelationship = false;

        if (this.model.beneficiaryDesignation.beneDesigMinorChild === "Y") {
            this.beneficiaryModel.lovBeneRelationshipId = BeneficiaryRelation.MinorDependentChild;
            this.beneficiaryCurrentState!.readOnlyRelationship = true;
        }
        if (this.beneficiaryCurrentState?.isMarriedOrRegisteredPartnership
            && this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance
            && this.model.beneficiaryDesignation?.beneficiaries?.length! > 1
            && this.beneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild) {
            this.beneficiaryCurrentState!.readOnlyRelationship = true;
        }
    }
    @action async editBeneficiaryAsync(index: number): Promise<void> {
        this.beneficiaryCurrentState!.validationMessage = "";
        this.beneficiaryCurrentState!.isEditingBeneficiary = true;
        this.beneficiaryCurrentState!.selectedBeneficiary = index;
        if (this.model.beneficiaryDesignation.beneficiaries) {
            this.beneficiaryModel = this.model.beneficiaryDesignation.beneficiaries[index] as BeneficiaryModel;
        }

        this.changeReadOnlyRelationshipStatus();

        this.setMemberDeclarationTextAndAddButtonVisibility();
    }

    @action async removeBeneficiaryAsync(index: number): Promise<void> {
        this.beneficiaryCurrentState!.selectedBeneficiary = index;
        if (this.beneficiaryCurrentState!.selectedBeneficiary! > -1 && this.model.beneficiaryDesignation.beneficiaries && this.model.beneficiaryDesignation.beneficiaries.length > 0) {
            let ben = this.model.beneficiaryDesignation.beneficiaries[this.beneficiaryCurrentState!.selectedBeneficiary!];
            const benDeleteResponse = await this._service?.deleteBeneficiaryAsync(ben.afrBeneId!);
            if (benDeleteResponse.success) {
                runInAction(() => {
                    this.model.beneficiaryDesignation.beneficiaries?.splice(this.beneficiaryCurrentState!.selectedBeneficiary!, 1);
                });
                await this.updateAutoUpdatableBeneficiaryPercentageAsync(true);
            } else {
                Appl.Error.handle(benDeleteResponse.error, true);
            }
        }
        this.beneficiaryCurrentState!.selectedBeneficiary = -1;
        this.setBeneficiaryBenefitCurrentState();
        this.setMemberDeclarationTextAndAddButtonVisibility();
        Appl.Validator.init();
    }

    @action async cancelAddBeneficiaryAsync(): Promise<void> {
        this.beneficiaryCurrentState!.validationMessage = "";
        if (this.beneficiaryCurrentState!.isAddingBeneficiary) {
            if (this.beneficiaryCurrentState!.selectedBeneficiary! > -1 && this.model.beneficiaryDesignation.beneficiaries && this.model.beneficiaryDesignation.beneficiaries.length > 0) {
                this.model.beneficiaryDesignation.beneficiaries.splice(this.beneficiaryCurrentState!.selectedBeneficiary!, 1);
            }
        }
        this.beneficiaryCurrentState!.isAddingBeneficiary = false;
        this.beneficiaryCurrentState!.isEditingBeneficiary = false;
        this.beneficiaryCurrentState!.selectedBeneficiary = -1;
        this.setBeneficiaryBenefitCurrentState();
        this.setMemberDeclarationTextAndAddButtonVisibility();
        Appl.Validator.init();
    }

    @action
    public async createOrUpdateBeneficiaryAsync(): Promise<boolean> {
        Appl.Validator.init();
        this.beneficiaryCurrentState!.validationMessage = "";
        this.beneficiaryCurrentState!.memberDeclarationVisible = false;
        this.model.beneficiaryDesignation.submissionId = this.submissionId;
        await Appl.Validator.validateNumber('Relationship', `lovBeneRelationshipId`, this.beneficiaryModel.lovBeneRelationshipId, true);
        if (this.beneficiaryCurrentState!.manuallyProvidePercentage) {
            await Appl.Validator.validateNumber('Percentage', `percentage`, this.beneficiaryModel.percentage, true, 100, 1);
        }
        if (this.beneficiaryModel.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg) {//Trust/Organization
            Appl.Validator.validateDate("Date of Birth", "beneBirthDate", this.beneficiaryModel.beneBirthDate, true, 'mm/dd/yyyy');
            if (Appl.Validator.isValid()) {
                if (new Date(this.beneficiaryModel.beneBirthDate!) >= new Date()) {
                    await Appl.Validator.setError("beneBirthDate", "Birth Date should not be greater than today date.");
                }
            }
            await Appl.Validator.validateString('First Name', `beneFirstName`, this.beneficiaryModel.beneFirstName, true, 100);
            await Appl.Validator.validateString('Last Name', `beneLastName`, this.beneficiaryModel.beneLastName, true, 100);
            await Appl.Validator.validateNumber('Social Security Number', `beneSsn`, this.beneficiaryModel.beneSsn, true, 999999999, 1, 9, 9);
            this.beneficiaryModel.beneOtherName = "";
            this.beneficiaryModel.beneTin = "";
        } else {
            await Appl.Validator.validateString('Entity Name', `beneOtherName`, this.beneficiaryModel.beneOtherName, true, 50);
            await Appl.Validator.validateNumber('Tax Identification Number', `beneTin`, this.beneficiaryModel.beneTin, false, 999999999, 1, 9, 9);
            this.beneficiaryModel.beneFirstName = "";
            this.beneficiaryModel.beneLastName = "";
            this.beneficiaryModel.beneSsn = "";
            this.beneficiaryModel.beneBirthDate = undefined;
        }
        await Appl.Validator.validateString('Address Line 1', 'beneStreetAddress', this.beneficiaryModel.beneStreetAddress, true, 200);
        await Appl.Validator.validateString('City', 'beneCity', this.beneficiaryModel.beneCity, true, 50);
        await Appl.Validator.validateString('State', 'beneState', this.beneficiaryModel.beneState, true, 50);
        await Appl.Validator.validateString('Country', 'beneCountry', this.beneficiaryModel.beneCountry, true, 50);
        if (this.beneficiaryModel.beneCountry === 'United States') {
            await Appl.Validator.validateZip('Zip', 'beneZip', this.beneficiaryModel.beneZip, true, 5);
        } else {
            await Appl.Validator.validateString('Zip', 'beneZip', this.beneficiaryModel.beneZip, true, 10);
        }
        if (!Appl.Validator.isValid()) {
            return false;
        }

        this.beneficiaryModel.beneBirthDate = toServerDate(this.beneficiaryModel.beneBirthDate);
        this.beneficiaryModel.afrMbrAffidavitId = this.model.beneficiaryDesignation.afrMbrAffidavitId;
        this.beneficiaryModel.beneType = 'R';
        const result = await this._service?.createOrUpdateBeneficiaryAsync(this.beneficiaryModel);
        if (!result.success) {
            let error = Appl.Error.getValidationError(result.error);
            if (error.pointer === "beneStreetAddress") {
                this.beneficiaryCurrentState!.validationMessageTitle = "Invalid Address";
                this.beneficiaryCurrentState!.validationMessage = "We couldn’t verify the address you entered. Check for any missing or incorrect details, such as street number, city, state, or ZIP code, and try again."
            } else {
                this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
                this.beneficiaryCurrentState!.validationMessage = error?.detail!;
            }
            return false
        } else {
            runInAction(() => {
                this.beneficiaryModel.afrBeneId = result.data.afrBeneId;
                this.beneficiaryModel.beneStreetAddress = result.data.beneStreetAddress;
                this.beneficiaryModel.beneStreetAddress2 = result.data.beneStreetAddress2;
                this.beneficiaryModel.beneCity = result.data.beneCity;
                this.beneficiaryModel.beneState = result.data.beneState;
                this.beneficiaryModel.beneZip = result.data.beneZip;
            })
        }
        this.beneficiaryCurrentState!.isAddingBeneficiary = false;
        this.beneficiaryCurrentState!.isEditingBeneficiary = false;
        this.beneficiaryCurrentState!.selectedBeneficiary = -1;
        this.setMemberDeclarationTextAndAddButtonVisibility();
        return true;
    }

    @action
    public async updateAutoUpdatableBeneficiaryPercentageAsync(save: boolean): Promise<void> {
        if (!this.beneficiaryCurrentState!.manuallyProvidePercentage) {
            let bens = this.model.beneficiaryDesignation.beneficiaries;
            if (bens) {
                let percent = 0;
                if (bens.length > 0) {
                    percent = 100 / bens.length;
                }
                for (var i = 0; i < bens.length; i++) {
                    bens[i].percentage = parseFloat(percent.toString());
                    if (save) {
                        const result = await this._service?.createOrUpdateBeneficiaryAsync(bens[i]);
                        if (!result.success) {
                            Appl.Error.handle(result.error, true);
                        }
                    }
                }
            }
        }
    }

    @action
    public isMarriedLessThanYear(): boolean {
        let marriageDate: (string & Date) | null;
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Married) {
            if (typeof this.model.personalInformation.marriageDate === "string") {
                marriageDate = new Date(this.model.personalInformation.marriageDate) as string & Date;
            } else {
                marriageDate = this.model.personalInformation.marriageDate!;
            }
        }
        let marriedForLessThanYear = this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Married
            && Helper.getYears(marriageDate!, new Date(this.model.personalInformation.retirementEffDate!)) < 1;

        return marriedForLessThanYear;

    }

    @action
    public isMarriedMoreThanYear(): boolean {
        let marriageDate: (string & Date) | null;
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Married) {
            if (typeof this.model.personalInformation.marriageDate === "string") {
                marriageDate = new Date(this.model.personalInformation.marriageDate) as string & Date;
            } else {
                marriageDate = this.model.personalInformation.marriageDate!;
            }
        }
        let marriedForMoreThanYear = this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Married
            && Helper.getYears(marriageDate!, new Date(this.model.personalInformation.retirementEffDate!)) >= 1;
        return marriedForMoreThanYear;

    }

    @action
    public isPartneredLessThanYear(): boolean {
        let partnershipDate: string & Date | null;
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.RegistedDomesticPartner) {
            if (typeof this.model.personalInformation.partnershipDate === "string") {
                partnershipDate = new Date(this.model.personalInformation.partnershipDate) as string & Date;
            } else {
                partnershipDate = this.model.personalInformation.partnershipDate!
            }
        }
        let registedDomesticPartnerForLessThanYear = this.model.personalInformation.lovPersonalStatusId === PersonalStatus.RegistedDomesticPartner &&
            Helper.getYears(partnershipDate!, new Date(this.model.personalInformation.retirementEffDate!)) < 1;

        return registedDomesticPartnerForLessThanYear;
    }

    @action
    public isPartneredMoreThanYear(): boolean {
        let partnershipDate: string & Date | null;
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.RegistedDomesticPartner) {
            if (typeof this.model.personalInformation.partnershipDate === "string") {
                partnershipDate = new Date(this.model.personalInformation.partnershipDate) as string & Date;
            } else {
                partnershipDate = this.model.personalInformation.partnershipDate!
            }
        }
        let registedDomesticPartnerForMoreThanYear = this.model.personalInformation.lovPersonalStatusId === PersonalStatus.RegistedDomesticPartner &&
            Helper.getYears(partnershipDate!, new Date(this.model.personalInformation.retirementEffDate!)) >= 1;
        return registedDomesticPartnerForMoreThanYear;
    }

    @action
    public isSingleWidowedDivorcedTdp(): boolean {
        let singleWidowedDivorcedTdp = this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Single
            || this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Widowed
            || this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Divorced
            || this.model.personalInformation.lovPersonalStatusId === PersonalStatus.TerminatedDomesticPartnership;
        return singleWidowedDivorcedTdp;
    }

    @action
    public setBeneficiaryBenefitCurrentState(): void {

        if (this.model.beneficiaryDesignation.beneficiaries?.length! === 0) {
            this.beneficiaryCurrentState!.readMoreVisible = true;
        }
        let items = this.model.beneficiaryRelationListItems;
        if (!items) {
            items = []
        }
        this.beneficiaryCurrentState!.isMarriedOrRegisteredPartnership = false;
        this.beneficiaryCurrentState!.manuallyProvidePercentage = false;
        this.beneficiaryCurrentState!.showDesignateMinorChildOptions = false;

        if (this.isMarriedMoreThanYear()) {
            this.beneficiaryCurrentState!.isMarriedOrRegisteredPartnership = true;
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.MinorDependentChild));
                this.beneficiaryCurrentState!.pageTitle = `Since you have been <b>married for at least one year</b> by your chosen retirement date and you’ve selected the <b>Unmodified Allowance</b> option, you may designate your spouse <b>OR</b> minor child(ren) as your beneficiary, to receive a monthly continuance.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Spouse: Your spouse will receive 60% of your monthly allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance), payable for their lifetime.</li>
                    <li class='pb-4'>For Your Minor Child(ren): Collectively, your minor child(ren) will receive a monthly allowance equal to 60% of your Service Retirement allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance). This benefit is payable until the child reaches age 18, age 22 if they are a full-time student, or marries—whichever occurs first. Since you are married spousal consent is required to designate your minor child(ren) as your beneficiary.</li>
                    <li class='pb-4'>Any refundable member contributions held by SCERS at the time of your death will also be paid to your designated retirement benefit beneficiary.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.MinorDependentChild
                    || o.key === BeneficiaryRelation.EntityTrustOrg
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 1</b>, you may designate your spouse, child(ren), or any individual(s) with insurable interest as your beneficiary(ies) to receive a refund of any remaining contributions and interest (if any). Since you are <b>married</b>, <b>spousal consent</b> is required to designate someone other than your spouse as a beneficiary.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary(ies): Under Option 1, a lumpsum payment equal to the remaining balance of your contributions and interest (if any) will be paid to your designated beneficiary(ies) upon your death. <b>This option does not provide ongoing monthly payments</b>.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>married</b> and you’ve selected <b>Option 2</b>, you may designate your spouse or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your spouse as a beneficiary, spousal consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 2, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 100% of your monthly allowance after your death, payable for their lifetime.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>married</b> and you’ve selected <b>Option 3</b>, you may designate your spouse or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your spouse as a beneficiary, spousal consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 3, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 50% of your monthly allowance after your death, payable for their lifetime.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 4</b>, you may designate anyone with an insurable interest in your life as a beneficiary. Under Option 4, the benefit you specify for your designated beneficiary(ies) - whether a dollar amount or percentage - requires actuarial calculations to determine your retirement allowance.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'><b>Important Note:</b> The calculations for Option 4 are more complex and will take additional time to complete. This will extend both the processing of your retirement calculation and the time it takes to receive your first monthly retirement allowance payment from SCERS.</li>`;
            }
        } else if (this.isMarriedLessThanYear()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = false;
                if (this.model.beneficiaryDesignation.beneDesigMinorChild === "Y") {
                    items = items.filter(o => o.key === BeneficiaryRelation.MinorDependentChild);
                } else if (this.model.beneficiaryDesignation.beneDesigMinorChild === "N") {
                    this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                    items = items.filter(o => (
                        o.key === BeneficiaryRelation.InsurableInterest
                        || o.key === BeneficiaryRelation.EntityTrustOrg));
                } else {
                    items = items.filter(o => (
                        o.key === BeneficiaryRelation.MinorDependentChild
                        || o.key === BeneficiaryRelation.InsurableInterest
                        || o.key === BeneficiaryRelation.EntityTrustOrg
                    ));
                }

                this.beneficiaryCurrentState!.pageTitle = `Since you have been <b>married for less than one year</b> by your chosen retirement date and  you’ve selected the <b>Unmodified Allowance</b> option, you may only designate <b>your minor child(ren) to receive a monthly continuance</b>.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Minor Child(ren): Collectively, your minor child(ren) will receive a monthly allowance equal to 60% of your Service Retirement allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance). This benefit is payable until the child reaches age 18, or age 22 if they are a full-time student, or marries-whichever occurs first.</li>
                <li class='pb-4'>If you do not have any minor children, then <b>no continuing monthly benefit can be paid to a beneficiary under this option</b>. However, even if no continuance is payable, you may still designate a beneficiary(ies) to receive a refund of any remaining member contributions and interest (if any) held by SCERS at the time of your death.</li>`;
                this.beneficiaryCurrentState!.NoEligibleBeneMessage = `No, I do not have eligible minor child(ren) and wish to proceed to designate a beneficiary(ies) for a refund of any remaining contributions (if any).`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.EntityTrustOrg
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 1</b>, you may designate your spouse, child(ren), or any individual(s) with insurable interest as your beneficiary(ies) to receive a refund of any remaining contributions and interest (if any).  Since you are <b>married</b>, <b>spousal consent</b> is required to designate someone other than your spouse as a beneficiary.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary(ies): Under Option 1, a lumpsum payment equal to the remaining balance of your contributions and interest (if any) will be paid to your designated beneficiary(ies) upon your death. <b>This option does not provide ongoing monthly payments</b>.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>married</b> and you’ve selected <b>Option 2</b>, you may designate your spouse or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your spouse as a beneficiary, spousal consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 2, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 100% of your monthly allowance after your death, payable for their lifetime.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>married</b> and you’ve selected <b>Option 3</b>, you may designate your spouse or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your spouse as a beneficiary, spousal consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 3, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 50% of your monthly allowance after your death, payable for their lifetime.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.Spouse
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 4</b>, you may designate anyone with an insurable interest in your life as a beneficiary. Under Option 4, the benefit you specify for your designated beneficiary(ies) - whether a dollar amount or percentage - requires actuarial calculations to determine your retirement allowance.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'><b>Important Note:</b> The calculations for Option 4 are more complex and will take additional time to complete. This will extend both the processing of your retirement calculation and the time it takes to receive your first monthly retirement allowance payment from SCERS.</li>`;
            }
        }
        else if (this.isPartneredMoreThanYear()) {
            this.beneficiaryCurrentState!.isMarriedOrRegisteredPartnership = true;
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {//Unmodified Allowance
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.MinorDependentChild));
                this.beneficiaryCurrentState!.pageTitle = `Since you have been <b>in a registered domestic partnership for at least one year</b> by your chosen retirement date and you’ve selected the <b>Unmodified Allowance</b> option, you may designate your registered domestic partner <b>OR</b> minor child(ren) as your beneficiary.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Registered Domestic Partner: Your registered domestic partner will receive 60% of your monthly allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance), payable for their lifetime.</li>
                    <li class='pb-4'>For Your Minor Child(ren): Collectively, your minor child(ren) will receive a monthly allowance equal to 60% of your Service Retirement allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance). This benefit is payable until the child reaches age 18, age 22 if they are a full-time student, or marries-whichever occurs first. Since you are <b>in a registered domestic partnership</b> consent from your registered domestic partner is required to designate your minor child(ren) as your beneficiary.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.EntityTrustOrg
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 1</b>, you may designate your registered domestic partner, child(ren), or any individual(s) with insurable interest as your beneficiary(ies) to receive a refund of any remaining contributions and interest (if any). As you are in a <b>registered domestic partnership</b>, <b>partner consent</b> is required to designate someone other than your partner as a beneficiary.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary(ies): Under Option 1, a lump-sum payment equal to the remaining balance of your contributions and interest (if any) will be paid to your designated beneficiary(ies) upon your death. <b>This option does not provide ongoing monthly payments</b>.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {//Option 2: 100% Joint and Survivor Annuity
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>in a registered domestic partnership</b> and you’ve selected <b>Option 2</b>, you may designate your registered domestic partner or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your registered domestic partner as a beneficiary, registered domestic partner consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 2, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 100% of your monthly allowance after your death, payable for their lifetime.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {//Option 3: 50% Joint and Survivor Annuity
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>in a registered domestic partnership</b> and you’ve selected <b>Option 3</b>, you may designate your registered domestic partner or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your registered domestic partner as a beneficiary, registered domestic partner consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 3, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 50% of your monthly allowance after your death, payable for their lifetime.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 4</b>, you may designate anyone with an insurable interest in your life as a beneficiary. Under Option 4, the benefit you specify for your designated beneficiary(ies) - whether a dollar amount or percentage - requires actuarial calculations to determine your retirement allowance.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'><b>Important Note:</b> The calculations for Option 4 are more complex and will take additional time to complete. This will extend both the processing of your retirement calculation and the time it takes to receive your first monthly retirement allowance payment from SCERS.</li>`;
            }
        } else if (this.isPartneredLessThanYear()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {//Unmodified Allowance
                this.beneficiaryCurrentState!.manuallyProvidePercentage = false;
                if (this.model.beneficiaryDesignation.beneDesigMinorChild === "Y") {
                    items = items.filter(o => o.key === BeneficiaryRelation.MinorDependentChild);
                } else if (this.model.beneficiaryDesignation.beneDesigMinorChild === "N") {
                    this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                    items = items.filter(o => (
                        o.key === BeneficiaryRelation.InsurableInterest
                        || o.key === BeneficiaryRelation.EntityTrustOrg));
                } else {
                    items = items.filter(o => (
                        o.key === BeneficiaryRelation.MinorDependentChild
                        || o.key === BeneficiaryRelation.InsurableInterest
                        || o.key === BeneficiaryRelation.EntityTrustOrg
                    ));
                }
                this.beneficiaryCurrentState!.pageTitle = `Since you have been <b>registered as a domestic partner for less than one year</b> by your chosen retirement date and you’ve selected the <b>Unmodified Allowance</b> option, you may only designate your <b>minor child(ren) to receive a monthly continuance</b>.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Minor Child(ren): Collectively, your minor child(ren) would receive a monthly allowance equal to 60% of your Service Retirement allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance). This benefit is payable until the child reaches age 18, or age 22 if they remain enrolled as a full-time student, or marries—whichever occurs first.</li>
                    <li class='pb-4'>If you do not have any eligible minor child(ren), then <b>no continuing monthly benefit can be paid under this option</b>. However, even if no continuance is payable, you may still designate a beneficiary(ies) to receive a refund of any remaining member contributions and interest held by SCERS at the time of your death.</li>`;
                this.beneficiaryCurrentState!.NoEligibleBeneMessage = `No, I do not have eligible minor child(ren) and wish to proceed to designate a beneficiary(ies) for a refund of any remaining contributions (if any) refundable contributions.`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.EntityTrustOrg
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 1</b>, you may designate your registered domestic partner, child(ren), or any individual(s) with insurable interest as your beneficiary(ies), for a refund of any remaining contributions and interest (if any).  As you are in a <b>registered domestic partnership</b>, <b>partner consent</b> is required to designate someone other than your partner as a beneficiary.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary(ies): Under Option 1, a lump-sum payment equal to the remaining balance of your contributions and interest (if any) will be paid to your designated beneficiary(ies) upon your death. <b>This option does not provide ongoing monthly payments</b>.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {//Option 2: 100% Joint and Survivor Annuity
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>in a registered domestic partnership</b> and you’ve selected <b>Option 2</b>, you may designate your registered domestic partner or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your registered domestic partner as a beneficiary, registered domestic partner consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 2, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 100% of your monthly allowance after your death, payable for their lifetime.</li>`;
            }
            else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {//Option 3: 50% Joint and Survivor Annuity
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>in a registered domestic partnership</b> and you’ve selected <b>Option 3</b>, you may designate your registered domestic partner or another individual with an insurable interest as your beneficiary. If you choose to designate someone other than your registered domestic partner as a beneficiary, partner consent is required.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 3, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 50% of your monthly allowance after your death, payable for their lifetime.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.RegisteredDomesticPartner
                    || o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 4</b>, you may designate anyone with an insurable interest in your life as a beneficiary. Under Option 4, the benefit you specify for your designated beneficiary(ies) - whether a dollar amount or percentage - requires actuarial calculations to determine your retirement allowance.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'><b>Important Note:</b> The calculations for Option 4 are more complex and will take additional time to complete. This will extend both the processing of your retirement calculation and the time it takes to receive your first monthly retirement allowance payment from SCERS.</li>`;
            }
        } else if (this.isSingleWidowedDivorcedTdp()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = false;
                if (this.model.beneficiaryDesignation.beneDesigMinorChild === "Y") {
                    items = items.filter(o => o.key === BeneficiaryRelation.MinorDependentChild);
                } else if (this.model.beneficiaryDesignation.beneDesigMinorChild === "N") {
                    this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                    items = items.filter(o => (
                        o.key === BeneficiaryRelation.InsurableInterest
                        || o.key === BeneficiaryRelation.EntityTrustOrg));
                } else {
                    items = items.filter(o => (
                        o.key === BeneficiaryRelation.MinorDependentChild
                        || o.key === BeneficiaryRelation.InsurableInterest
                        || o.key === BeneficiaryRelation.EntityTrustOrg));
                }
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>single</b> and you’ve selected the <b>Unmodified Allowance</b> option, you may only designate <b>your minor child(ren) to receive a monthly continuance</b>.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Minor Child(ren): Collectively, your minor child(ren) will receive a monthly allowance equal to 60% of your Service Retirement allowance at the time of your death (or 100% of your Service-Connected Disability Retirement allowance). This benefit is payable until the child reaches age 18, or age 22 if they are a full-time student, or marries-whichever occurs first.</li>
                <li class='pb-4'>If you do not have any minor children, then <b>no continuing monthly benefit can be paid to a beneficiary under this option</b>. However, even if no continuance is payable, you may still designate a beneficiary(ies) to receive a refund of any remaining member contributions and interest held by SCERS at the time of your death.</li>`;
                this.beneficiaryCurrentState!.NoEligibleBeneMessage = `No, I do not have eligible minor child(ren) and wish to proceed to designate a beneficiary(ies) for a refund of any remaining contributions and interest (if any).`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (
                    o.key === BeneficiaryRelation.InsurableInterest
                    || o.key === BeneficiaryRelation.EntityTrustOrg));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>single</b> and have selected <b>Option 1</b>, you may designate your child(ren) or any individual with insurable interest as your beneficiary. You may also designate multiple beneficiaries and allocate the lump-sum benefit among them.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiaryies): Under Option 1, a lumpsum payment equal to the remaining balance of your contributions and interest (if any) will be paid to your designated beneficiary(ies) upon your death. <b>This option does not provide ongoing monthly payments</b>.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                items = items.filter(o => (o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>single</b> and you’ve selected <b>Option 2</b>, you may designate any individual with an insurable interest as your beneficiary. `;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 2, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 100% of your monthly allowance after your death, payable for their lifetime.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                items = items.filter(o => (o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you are <b>single</b> and you’ve selected <b>Option 3</b>, you may designate any individual with an insurable interest as your beneficiary.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'>For Your Beneficiary: Under Option 3, your monthly retirement allowance will be reduced during your lifetime to provide your designated beneficiary with 50% of your monthly allowance after your death, payable for their lifetime.</li>`;
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.manuallyProvidePercentage = true;
                items = items.filter(o => (o.key === BeneficiaryRelation.InsurableInterest));
                this.beneficiaryCurrentState!.pageTitle = `Since you have selected <b>Option 4</b>, you may designate anyone with an insurable interest in your life as a beneficiary. Under Option 4, the benefit you specify for your designated beneficiary(ies) - whether a dollar amount or percentage - requires actuarial calculations to determine your retirement allowance.`;
                this.beneficiaryCurrentState!.pageOption = `<li class='pb-4'><b>Important Note:</b> The calculations for Option 4 are more complex and will take additional time to complete. This will extend both the processing of your retirement calculation and the time it takes to receive your first monthly retirement allowance payment from SCERS.</li>`;
            }
        }
        this.beneficiaryCurrentState!.relationships = items;
        this.setMemberDeclarationTextAndAddButtonVisibility();
    }

    @action
    public async onSetDesignateMinorChildOptionsAsync(): Promise<boolean> {
        const result = await this._service?.setBeneficiaryDesignationMinorChildStatusAsync(this.submissionId!, this.model.beneficiaryDesignation.beneDesigMinorChild!);
        if (result.success) {
            this.setBeneficiaryBenefitCurrentState();
            await this.addBeneficiaryAsync()
            if (this.model.beneficiaryDesignation.beneDesigMinorChild === "Y") {
                this.beneficiaryModel.lovBeneRelationshipId = BeneficiaryRelation.MinorDependentChild;
                this.beneficiaryCurrentState!.readOnlyRelationship = true;
            } else {
                this.beneficiaryModel.lovBeneRelationshipId = undefined;
                this.beneficiaryCurrentState!.readOnlyRelationship = false;
            }

        } else {
            Appl.Error.handle(result.error, true);
        }
        return true;
    }

    @action
    public async onValidateBeneficiaryStepAsync(calledFromReviewScreen: boolean = false): Promise<boolean> {
        if (calledFromReviewScreen) {
            if (this.previousLovPersonalStatusId != this.model.personalInformation.lovPersonalStatusId) {
                return false;
            }
            this.setBeneficiaryBenefitCurrentState();
        }

        if (this.isMarriedMoreThanYear() &&
            this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
            if (this.model.beneficiaryDesignation.beneficiaries?.length! > 1) {
                let spouseBene = this.model.beneficiaryDesignation.beneficiaries?.filter(o =>
                    o.lovBeneRelationshipId === BeneficiaryRelation.Spouse);
                if (spouseBene?.length! > 0) {
                    this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
                    this.beneficiaryCurrentState!.validationMessage = `You've made a change to your personal status or retirement option. To make sure your beneficiary designations are accurate, you'll need to review and update them before continuing.`;
                    return false;
                }
            }
        }
        if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2
            || this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3
        ) {
            if (this.model.beneficiaryDesignation.beneficiaries?.length! > 1) {
                this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
                this.beneficiaryCurrentState!.validationMessage = `Only one beneficiary is allowed for this Option.`;
                return false;
            }
        }
        for (var i = 0; i < this.model.beneficiaryDesignation.beneficiaries?.length!; i++) {
            let selectedBene = this.model.beneficiaryDesignation.beneficiaries![i];
            let allBene = this.beneficiaryCurrentState!.relationships?.find(o => o.key === selectedBene.lovBeneRelationshipId);
            if (!allBene) {
                this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
                this.beneficiaryCurrentState!.validationMessage = `You've made a change to your personal status or retirement option. To make sure your beneficiary designations are accurate, you'll need to review and update them before continuing.`;
                return false;
            }
        }
        await this.updateAutoUpdatableBeneficiaryPercentageAsync(false);
        this.beneficiaryCurrentState!.validationMessage = "";
        this.beneficiaryCurrentState!.memberDeclarationVisible = false;
        let percentage: number = 0;
        let childPercent: number = 0;
        let bens = this.model.beneficiaryDesignation.beneficiaries;//?.filter(o =>o.lovBeneRelationshipId !== BeneficiaryRelation.NoEligibleBeneficiary);
        if (bens) {
            for (var i = 0; i < bens.length; i++) {
                let beneficiary = bens[i];
                if (beneficiary.percentage) {
                    percentage += parseFloat(beneficiary.percentage.toString());
                }
                if (beneficiary.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild) {
                    if (beneficiary.percentage) {
                        if (childPercent > 0) {
                            if (childPercent !== parseFloat(beneficiary.percentage.toString()!)) {
                                this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
                                this.beneficiaryCurrentState!.validationMessage = "When you add multiple beneficiaries as minor/dependent child(ren), percentages should be equal among dependent child(ren)</b>. Please adjust your allocations accordingly to ensure minor/dependent benefits are fully distributed.";
                                return false;
                            }
                        }
                        childPercent = parseFloat(beneficiary.percentage.toString()!);
                    }
                }
            }
        }
        if (percentage != 100) {
            this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
            this.beneficiaryCurrentState!.validationMessage = "The total percentage allocated to your beneficiaries <b>does not equal 100%</b>. Please adjust your allocations accordingly to ensure your benefits are fully distributed.";
            return false;
        }
        if (!calledFromReviewScreen) {
            this.beneficiaryCurrentState!.memberDeclarationVisible = true
            await this.updateAutoUpdatableBeneficiaryPercentageAsync(true);
            this.setMemberDeclarationTextAndAddButtonVisibility();
        }
        return true;
    }

    public setMemberDeclarationTextAndAddButtonVisibility(): void {

        let memberDeclarationText = '';

        this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
        this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
        this.beneficiaryCurrentState!.showDesignateMinorChildOptions = false;

        this.beneficiaryCurrentState!.addNewButtonText = "Add Another Beneficiary";
        if (this.model.beneficiaryDesignation?.beneficiaries?.length === 0) {
            this.beneficiaryCurrentState!.addNewTopButtonVisible = true;
            this.beneficiaryCurrentState!.addNewButtonText = "Add A Beneficiary";
        }
        if (this.model.beneficiaryDesignation?.beneficiaries?.length! > 0) {
            if (this.model.beneficiaryDesignation?.beneficiaries?.length! % 2 === 0) {//Even Boxes
                this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                this.beneficiaryCurrentState!.addNewTopButtonVisible = true;

            } else {
                this.beneficiaryCurrentState!.addNewSideButtonVisible = true;
                this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
            }
        }
        this.updateAutoUpdatableBeneficiaryPercentageAsync(false);
        let spouseDesignated100 = this.isSpouseSelectedAs100PercentBenefitiary();
        let rdpDesignated100 = this.isRDPSelectedAs100PercentBenefitiary();
        if (this.isMarriedMoreThanYear()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent – Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                } else {
                    if (this.beneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild) {
                        this.beneficiaryCurrentState!.addNewButtonText = "Add Another Minor Child Beneficiary";
                    }
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, l am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            }

        } else if (this.isMarriedLessThanYear()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, l am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! === 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.showDesignateMinorChildOptions = true;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (spouseDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            }
        } else if (this.isPartneredMoreThanYear()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                } else {
                    if (this.beneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild) {
                        this.beneficiaryCurrentState!.addNewButtonText = "Add Another Minor Child Beneficiary";
                    }
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            }
        } else if (this.isPartneredLessThanYear()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! === 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.showDesignateMinorChildOptions = true;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, l am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, l am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "N";
                memberDeclarationText = `I am married or registered as a domestic partner, and I have NOT named my spouse or registered domestic partner as my sole beneficiary under the Plan. I understand that my spouse or registered domestic partner must complete Section B: Required Consent - Current Spouse or Registered Domestic Partner Agreement to Alternate Beneficiary. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                if (rdpDesignated100) {
                    this.beneficiaryCurrentState!.memberDeclarationValue = "M";
                    memberDeclarationText = `I am married or registered as a domestic partner and I have named my spouse or registered domestic partner as sole beneficiary under the Plan. Beyond the interests of my current spouse or registered domestic partner, I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.`;
                }
            }
        } else if (this.isSingleWidowedDivorcedTdp()) {
            if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "S";
                memberDeclarationText = 'I am single, widowed, divorced or terminated my domestic partnership, and I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.';
                if (this.model.beneficiaryDesignation.beneficiaries?.length! === 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.showDesignateMinorChildOptions = true;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option1) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "S";
                memberDeclarationText = 'I am single, widowed, divorced or terminated my domestic partnership, and I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.';
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option2) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "S";
                memberDeclarationText = 'I am single, widowed, divorced or terminated my domestic partnership, and I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.';
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option3) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "S";
                memberDeclarationText = 'I am single, widowed, divorced or terminated my domestic partnership, and I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.';
                if (this.model.beneficiaryDesignation.beneficiaries?.length! > 0) {
                    this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
                    this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
                }
            } else if (this.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.Option4) {
                this.beneficiaryCurrentState!.memberDeclarationValue = "S";
                memberDeclarationText = 'I am single, widowed, divorced or terminated my domestic partnership, and I am unaware of any undisclosed actions, agreements, or stipulations regarding my Plan benefits.';
            }
        }
        this.beneficiaryCurrentState!.memberDeclarationText = memberDeclarationText;
        if (this.beneficiaryCurrentState!.memberDeclarationVisible
            || this.beneficiaryCurrentState!.isAddingBeneficiary
            || this.beneficiaryCurrentState!.isEditingBeneficiary
        ) {
            this.beneficiaryCurrentState!.addNewTopButtonVisible = false;
            this.beneficiaryCurrentState!.addNewSideButtonVisible = false;
        }
        if (this.model.beneficiaryDesignation.afrDecleration !== this.beneficiaryCurrentState!.memberDeclarationValue) {
            this.model.beneficiaryDesignation.afrDecleration = "";
        }
    }

    public isSpouseSelectedAs100PercentBenefitiary(): boolean {
        let spouseDesignation = 0;
        this.model.beneficiaryDesignation.beneficiaries?.filter(o => o.lovBeneRelationshipId === BeneficiaryRelation.Spouse)
            .forEach(beneficiary => {
                let per = beneficiary.percentage?.toString();
                if (per !== "") {
                    spouseDesignation += parseFloat(per!);
                }
            })
        return spouseDesignation === 100;
    }

    public isRDPSelectedAs100PercentBenefitiary() {
        let rdpDesignation = 0;
        this.model.beneficiaryDesignation.beneficiaries?.filter(o => o.lovBeneRelationshipId === BeneficiaryRelation.RegisteredDomesticPartner)
            .forEach(beneficiary => {
                let per = beneficiary.percentage?.toString();
                if (per !== "") {
                    rdpDesignation += parseFloat(per!);
                }
            })
        return rdpDesignation === 100;
    }

    public async onBeneficiaryStepCompleteAsync(): Promise<boolean> {
        const response = await this._service?.setMemberAggreedDeclarationStatusAsync(this.submissionId!, this.model.beneficiaryDesignation.afrDecleration);
        if (response.success) {
            await this.setStepStatus(Steps.BeneficiaryBenefit, StepStatus.Completed);
            this.beneficiaryCurrentState!.memberDeclarationVisible = false;
            if (this.navigatedFromReviewScreen) {
                await this.navigateToReviewStepAsync();
            } else {
                await this.navigateToStepAsync(`${Steps.BeneficiaryDesignation}/${Steps.BeneficiaryBurialAllowance}`)
            }

        } else {
            Appl.Error.handle(response.error, true);
            return false;
        }
        return true;
    }
    @action
    public async cloneBeneficiaryBenefitToBurialAllowance(): Promise<void> {
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        if (this.beneficiaryBurialCurrentState?.wishOption === 'same') {
            let bens = this.model.beneficiaryDesignation.beneficiaries;
            if (bens) {
                for (var i = 0; i < bens.length; i++) {
                    let beneficiary = bens[i];
                    let burialBeneficiary: BeneficiaryModel = {
                        beneType: 'B',
                        afrBeneId: undefined,
                        afrMbrAffidavitId: beneficiary.afrMbrAffidavitId,
                        lovBeneRelationshipId: beneficiary.lovBeneRelationshipId,
                        beneBirthDate: toServerDate(beneficiary.beneBirthDate),
                        beneFirstName: beneficiary.beneFirstName,
                        beneLastName: beneficiary.beneLastName,
                        beneMiddleName: beneficiary.beneMiddleName,
                        beneOtherName: beneficiary.beneOtherName,
                        beneSsn: beneficiary.beneSsn,
                        beneTin: beneficiary.beneTin,
                        beneStreetAddress: beneficiary.beneStreetAddress,
                        beneStreetAddress2: beneficiary.beneStreetAddress2,
                        beneCity: beneficiary.beneCity,
                        beneState: beneficiary.beneState,
                        beneZip: beneficiary.beneZip,
                        beneCountry: beneficiary.beneCountry,
                        burialPercentage: beneficiary.percentage,
                    }
                    if (burialBeneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg
                    ) {
                        burialBeneficiary.lovBeneRelationshipId = BeneficiaryRelation.Person;
                    }
                    this.model.beneficiaryDesignation.burialBeneficiaries?.push(burialBeneficiary);
                    const result = await this._service?.createOrUpdateBeneficiaryAsync(burialBeneficiary);
                    if (!result.success) {
                        Appl.Error.handle(result.error, true);
                        return;
                    }
                }
                this.loadStepAsync(Steps.BeneficiaryBurialAllowance, this.memberId!);
            }
        } else {
            if (this.model.beneficiaryDesignation.burialBeneficiaries?.length === 0) {
                this.addBurialBeneficiaryAsync()
            }
        }
        this.showHideBurialBeneficiaryAddNewButton();
    }


    @action async addBurialBeneficiaryAsync(beneficiaryRelation?: BeneficiaryRelation.MinorDependentChild): Promise<void> {
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        this.beneficiaryBurialCurrentState!.isAddingBeneficiary = true;
        this.burialBeneficiaryModel = { beneCountry: 'United States' };
        if (this.model.beneficiaryDesignation.burialBeneficiaries) {
            this.model.beneficiaryDesignation.burialBeneficiaries.push({ beneType: 'B', afrMbrAffidavitId: this.model.beneficiaryDesignation.afrMbrAffidavitId });
            this.beneficiaryBurialCurrentState!.selectedBeneficiary = this.model.beneficiaryDesignation.burialBeneficiaries.length - 1;
            this.burialBeneficiaryModel = this.model.beneficiaryDesignation.burialBeneficiaries[this.beneficiaryBurialCurrentState!.selectedBeneficiary];
        }
        this.burialBeneficiaryModel.lovBeneRelationshipId = beneficiaryRelation;
        if (!this.burialBeneficiaryModel.beneCountry) {
            this.burialBeneficiaryModel.beneCountry = 'United States';
        }
        this.showHideBurialBeneficiaryAddNewButton();
    }

    @action async editBurialBeneficiaryAsync(index: number): Promise<void> {
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        this.beneficiaryBurialCurrentState!.isEditingBeneficiary = true;
        this.beneficiaryBurialCurrentState!.selectedBeneficiary = index;
        if (this.model.beneficiaryDesignation.burialBeneficiaries) {
            this.burialBeneficiaryModel = this.model.beneficiaryDesignation.burialBeneficiaries[index] as BeneficiaryModel;
        }
        this.showHideBurialBeneficiaryAddNewButton();
    }

    @action async removeBurialBeneficiaryAsync(index: number): Promise<void> {
        this.beneficiaryBurialCurrentState!.selectedBeneficiary = index;
        if (this.beneficiaryBurialCurrentState!.selectedBeneficiary! > -1 && this.model.beneficiaryDesignation.burialBeneficiaries && this.model.beneficiaryDesignation.burialBeneficiaries.length > 0) {
            let ben = this.model.beneficiaryDesignation.burialBeneficiaries[this.beneficiaryBurialCurrentState!.selectedBeneficiary!];
            const benDeleteResponse = await this._service?.deleteBeneficiaryAsync(ben.afrBeneId!);
            if (benDeleteResponse.success) {
                runInAction(() => {
                    this.model.beneficiaryDesignation.burialBeneficiaries?.splice(this.beneficiaryBurialCurrentState!.selectedBeneficiary!, 1);
                });
            } else {
                Appl.Error.handle(benDeleteResponse.error, true);
            }
        }
        this.beneficiaryBurialCurrentState!.selectedBeneficiary = -1;
        this.showHideBurialBeneficiaryAddNewButton();
        Appl.Validator.init();
    }

    @action async cancelAddBurialBeneficiaryAsync(): Promise<void> {
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        if (this.beneficiaryBurialCurrentState!.isAddingBeneficiary) {
            if (this.beneficiaryBurialCurrentState!.selectedBeneficiary! > -1 && this.model.beneficiaryDesignation.burialBeneficiaries && this.model.beneficiaryDesignation.burialBeneficiaries.length > 0) {
                this.model.beneficiaryDesignation.burialBeneficiaries.splice(this.beneficiaryBurialCurrentState!.selectedBeneficiary!, 1);
            }
        }
        this.beneficiaryBurialCurrentState!.isAddingBeneficiary = false;
        this.beneficiaryBurialCurrentState!.isEditingBeneficiary = false;
        this.beneficiaryBurialCurrentState!.selectedBeneficiary = -1;
        this.showHideBurialBeneficiaryAddNewButton();
        Appl.Validator.init();
    }

    @action
    public async createOrUpdateBurialBeneficiaryAsync(): Promise<boolean> {
        Appl.Validator.init();
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        this.beneficiaryBurialCurrentState!.summaryVisible = false;
        this.model.beneficiaryDesignation.submissionId = this.submissionId;
        await Appl.Validator.validateNumber('Relationship', `lovBeneRelationshipId`, this.burialBeneficiaryModel.lovBeneRelationshipId, true);
        await Appl.Validator.validateNumber('Percentage of Allowance', `burialPercentage`, this.burialBeneficiaryModel.burialPercentage, true, 100, 1);
        if (this.burialBeneficiaryModel.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg) {//Trust/Organization
            Appl.Validator.validateDate("Date of Birth", "beneBirthDate", this.burialBeneficiaryModel.beneBirthDate, true, 'mm/dd/yyyy');
            if (Appl.Validator.isValid()) {
                if (new Date(this.burialBeneficiaryModel.beneBirthDate!) >= new Date()) {
                    await Appl.Validator.setError("beneBirthDate", "Birth Date should not be greater than today date.");
                }
            }
            await Appl.Validator.validateString('First Name', `beneFirstName`, this.burialBeneficiaryModel.beneFirstName, true, 100);
            await Appl.Validator.validateString('Last Name', `beneLastName`, this.burialBeneficiaryModel.beneLastName, true, 100);
            await Appl.Validator.validateNumber('Social Security Number', `beneSsn`, this.burialBeneficiaryModel.beneSsn, true, 999999999, 1, 9, 9);
            this.burialBeneficiaryModel.beneOtherName = "";
            this.burialBeneficiaryModel.beneTin = "";
        } else {
            await Appl.Validator.validateString('Entity Name', `beneOtherName`, this.burialBeneficiaryModel.beneOtherName, true, 50);
            await Appl.Validator.validateNumber('Tax Identification Number', `beneTin`, this.burialBeneficiaryModel.beneTin, false, 999999999, 1, 9, 9);
            this.burialBeneficiaryModel.beneFirstName = "";
            this.burialBeneficiaryModel.beneLastName = "";
            this.burialBeneficiaryModel.beneSsn = "";
            this.burialBeneficiaryModel.beneBirthDate = undefined;
        }
        await Appl.Validator.validateString('Address Line 1', 'beneStreetAddress', this.burialBeneficiaryModel.beneStreetAddress, true, 200);
        await Appl.Validator.validateString('City', 'beneCity', this.burialBeneficiaryModel.beneCity, true, 50);
        await Appl.Validator.validateString('State', 'beneState', this.burialBeneficiaryModel.beneState, true, 50);
        await Appl.Validator.validateString('Country', 'beneCountry', this.burialBeneficiaryModel.beneCountry, true, 50);
        if (this.burialBeneficiaryModel.beneCountry === 'United States') {
            await Appl.Validator.validateZip('Zip', 'beneZip', this.burialBeneficiaryModel.beneZip, true, 5);
        } else {
            await Appl.Validator.validateString('Zip', 'beneZip', this.burialBeneficiaryModel.beneZip, true, 10);
        }
        if (!Appl.Validator.isValid()) {
            return false;
        }
        this.burialBeneficiaryModel.afrMbrAffidavitId = this.model.beneficiaryDesignation.afrMbrAffidavitId;
        this.burialBeneficiaryModel.beneBirthDate = toServerDate(this.burialBeneficiaryModel.beneBirthDate);
        this.burialBeneficiaryModel.beneType = 'B';
        const result = await this._service?.createOrUpdateBeneficiaryAsync(this.burialBeneficiaryModel);
        if (!result.success) {
            let error = Appl.Error.getValidationError(result.error);
            if (error.pointer === "beneStreetAddress") {
                this.beneficiaryBurialCurrentState!.validationMessageTitle = "Invalid Address";
                this.beneficiaryBurialCurrentState!.validationMessage = "We couldn’t verify the address you entered. Check for any missing or incorrect details, such as street number, city, state, or ZIP code, and try again."
            } else {
                this.beneficiaryBurialCurrentState!.validationMessageTitle = "Attention";
                this.beneficiaryBurialCurrentState!.validationMessage = error?.detail!;
            }
            return false
        } else {
            runInAction(() => {
                this.burialBeneficiaryModel.afrBeneId = result.data.afrBeneId;
                this.burialBeneficiaryModel.beneStreetAddress = result.data.beneStreetAddress;
                this.burialBeneficiaryModel.beneStreetAddress2 = result.data.beneStreetAddress2;
                this.burialBeneficiaryModel.beneCity = result.data.beneCity;
                this.burialBeneficiaryModel.beneState = result.data.beneState;
                this.burialBeneficiaryModel.beneZip = result.data.beneZip;
            })
        }
        this.beneficiaryBurialCurrentState!.isAddingBeneficiary = false;
        this.beneficiaryBurialCurrentState!.isEditingBeneficiary = false;
        this.beneficiaryBurialCurrentState!.selectedBeneficiary = -1;
        this.showHideBurialBeneficiaryAddNewButton();
        return true;
    }

    @action
    public async onValidateBeneficiaryBurialStepCompleteAsync(): Promise<boolean> {
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        let percentage: number = 0;
        // let childPercent: number = 0;
        let bens = this.model.beneficiaryDesignation.burialBeneficiaries;//?.filter(o => o.lovBeneRelationshipId !== BeneficiaryRelation.NoEligibleBeneficiary);
        if (bens) {
            for (var i = 0; i < bens.length; i++) {
                let beneficiary = bens[i];
                if (beneficiary.burialPercentage) {
                    percentage += parseFloat(beneficiary.burialPercentage.toString());
                }
            }
        }
        if (percentage != 100) {
            if (bens && bens.length! > 0) {
                this.beneficiaryCurrentState!.validationMessageTitle = "Attention";
                this.beneficiaryBurialCurrentState!.validationMessage = "The total percentage allocated to your beneficiaries <b>does not equal 100%</b>. Please adjust your allocations accordingly to ensure your benefits are fully distributed.";
                return false;
            }
        }
        this.beneficiaryBurialCurrentState!.summaryVisible = true;
        this.showHideBurialBeneficiaryAddNewButton();
        return true;
    }

    @action
    public async onBeneficiaryBurialStepCompleteAsync(): Promise<boolean> {
        this.beneficiaryBurialCurrentState!.validationMessage = "";
        await this.setStepStatus(Steps.BeneficiaryBurialAllowance, StepStatus.Completed);
        this.beneficiaryBurialCurrentState!.completed = true;
        this.beneficiaryBurialCurrentState!.summaryVisible = false;
        if (this.navigatedFromReviewScreen) {
            await this.navigateToReviewStepAsync();
        } else {
            await this.navigateToStepAsync(Steps.TaxWithholding);
        }
        return true;
    }

    @action public showHideBurialBeneficiaryAddNewButton(): void {
        if (this.model.beneficiaryDesignation?.burialBeneficiaries?.length === 0) {
            this.beneficiaryBurialCurrentState!.addNewTopButtonVisible = true;
        }
        if (this.model.beneficiaryDesignation?.burialBeneficiaries?.length! > 0) {
            if (this.model.beneficiaryDesignation?.burialBeneficiaries?.length! % 2 === 0) {//Even Boxes
                this.beneficiaryBurialCurrentState!.addNewSideButtonVisible = false;
                this.beneficiaryBurialCurrentState!.addNewTopButtonVisible = true;

            } else {
                this.beneficiaryBurialCurrentState!.addNewSideButtonVisible = true;
                this.beneficiaryBurialCurrentState!.addNewTopButtonVisible = false;
            }
        }
        if (this.beneficiaryBurialCurrentState!.isAddingBeneficiary
            || this.beneficiaryBurialCurrentState!.isEditingBeneficiary
            || this.beneficiaryBurialCurrentState?.summaryVisible) {
            this.beneficiaryBurialCurrentState!.addNewSideButtonVisible = false;
            this.beneficiaryBurialCurrentState!.addNewTopButtonVisible = false;
        }
    }

    @action public onFederalTaxWithholdStatusChange(status: string, checked: boolean): void {

        if (this.model.taxWithHolding.federalTaxWithHolding) {
            if (status === "N" && checked) {
                this.model.taxWithHolding.federalTaxWithHolding.federalTaxWithhold = "N";
            } else if (status === "Y" && checked) {
                this.model.taxWithHolding.federalTaxWithHolding.federalTaxWithhold = "Y";
            }
            if (status === "N" && checked) {
                if (this.model.taxWithHolding) {
                    if (this.model.taxWithHolding.federalTaxWithHolding) {
                        this.model.taxWithHolding.federalTaxWithHolding.federalWithholdIncomeTaxStatus = undefined
                        this.model.taxWithHolding.federalTaxWithHolding.federalClaimsDependant = undefined;
                        this.model.taxWithHolding.federalTaxWithHolding.federalIncomeNotFromJob = undefined;
                        this.model.taxWithHolding.federalTaxWithHolding.federalDeductionsClaimed = undefined;
                        this.model.taxWithHolding.federalTaxWithHolding.federalAdditionalWithold = undefined;
                    }
                }
            }
        }
    }

    @action public async onFederalTaxWithholdStepChangeAsync(): Promise<boolean> {
        Appl.Validator.init();
        if (this.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold === "Y") {
            if (this.model.taxWithHolding.federalTaxWithHolding?.federalWithholdIncomeTaxStatus === undefined
                || this.model.taxWithHolding.federalTaxWithHolding?.federalWithholdIncomeTaxStatus === '') {
                await Appl.Validator.setError('federalWithholdIncomeTaxStatus', 'Please select a filing status.');
            }
        }
        if (!Appl.Validator.isValid()) { return false; }
        const result = await this._service?.createOrUpdateFederalTaxWithholdingAsync(this.model.taxWithHolding.federalTaxWithHolding!);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.FederalTaxWithholding, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.TaxWithholding}/${Steps.StateTaxWithholding}`);
        return true;
    }

    @action public onStateTaxWithholdStatusChange(status: string, checked: boolean): void {

        if (this.model.taxWithHolding.stateTaxWithHolding) {
            if (status === "N" && checked) {
                this.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold = "N";
            } else if (status === "Y" && checked) {
                this.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold = "Y";

            } else if (status === "P" && checked) {
                this.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold = "P";
            }
            if ((status === "N" || status === "P") && checked) {
                if (this.model.taxWithHolding) {
                    if (this.model.taxWithHolding.stateTaxWithHolding) {
                        this.model.taxWithHolding.stateTaxWithHolding.stateWithholdIncomeTaxStatus = undefined;
                        this.model.taxWithHolding.stateTaxWithHolding.stateMarriedWithholdIncomeAllowance = undefined;
                        this.model.taxWithHolding.stateTaxWithHolding.stateSingleWithholdIncomeAllowance = undefined;
                        this.model.taxWithHolding.stateTaxWithHolding.stateHeadOfHouseHoldIncomeAllowance = undefined;
                        this.model.taxWithHolding.stateTaxWithHolding.stateAdditionalTaxToWithheldBasedOnTax = undefined;
                        this.model.taxWithHolding.stateTaxWithHolding.stateAdditionalTaxToWithheldBasedOnBenefitPayment = undefined;
                    }
                }
            }
            if ((status === "N" || status === "Y") && checked) {
                this.model.taxWithHolding.stateTaxWithHolding.stateAdditionalTaxToWithheldBasedOnBenefitPayment = undefined;
            }
        }
    }

    @action
    public async onStateTaxWithholdStepChangeAsync(): Promise<boolean> {
        Appl.Validator.init();
        if (this.model.taxWithHolding.stateTaxWithHolding) {
            if (this.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold == "Y") {
                if (this.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus === undefined ||
                    this.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus === "") {
                    await Appl.Validator.setError('withholdStateIncomeTaxStatus', 'Please select a filing status.');
                } else {
                    if (this.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus === "M") {
                        await Appl.Validator.validateNumber('Allowance', `stateMarriedWithholdIncomeAllowance`, this.model.taxWithHolding.stateTaxWithHolding?.stateMarriedWithholdIncomeAllowance, true);
                    } else if (this.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus === "S") {
                        await Appl.Validator.validateNumber('Allowance', `stateSingleWithholdIncomeAllowance`, this.model.taxWithHolding.stateTaxWithHolding?.stateSingleWithholdIncomeAllowance, true);
                    } else if (this.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus === "H") {
                        await Appl.Validator.validateNumber('Allowance', `stateHeadOfHouseHoldIncomeAllowance`, this.model.taxWithHolding.stateTaxWithHolding?.stateHeadOfHouseHoldIncomeAllowance, true);
                    }
                }
            } else if (this.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold == "P") {
                if (this.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnBenefitPayment === undefined
                    || this.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnBenefitPayment?.toString() === "") {
                    await Appl.Validator.validateNumber('Amount', `stateAdditionalTaxToWithheldBasedOnBenefitPayment`, this.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnBenefitPayment, true);
                }
            }
        }

        if (!Appl.Validator.isValid()) { return false; }
        const result = await this._service?.createOrUpdateStateTaxWithholdingAsync(this.model.taxWithHolding.stateTaxWithHolding!);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.StateTaxWithholding, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.TaxWithholding}/${Steps.DeclarationTaxWithholding}`);
        return true;
    }

    @action
    public async onTaxWithholdingMemberDeclarationCompletedAsync(agree: boolean): Promise<void> {
        const response = await this._service?.setTaxWithholdAggreedDeclarationStatusAsync(this.submissionId!, agree);
        if (response.success) {
            this.taxWithHoldingModelCompleted = true;
            await this.setStepStatus(Steps.DeclarationTaxWithholding, StepStatus.Completed);
            if (this.navigatedFromReviewScreen) {
                await this.navigateToReviewStepAsync();
            } else {
                await this.navigateToStepAsync(`${Steps.DirectDeposit}/${Steps.DirectDepositAuthorization}`);
            }
        } else {
            Appl.Error.handle(response.error, true);
        }
    }

    @action public async onDirectDepositAccountInfoStepChangeAsync(): Promise<boolean> {
        this.model.directDeposit.submissionId = this.submissionId;
        Appl.Validator.init();
        await Appl.Validator.validateString('Institution Name', 'institutionName', this.model.directDeposit.institutionName, true, 100);
        await Appl.Validator.validateString('Name of Account Holder', 'accountHolderName', this.model.directDeposit.accountHolderName, true, 100);
        await Appl.Validator.validateString('9-Digit Routing Number', 'bankRoutingNumeric', this.model.directDeposit.bankRoutingNumeric, true, 9);
        await Appl.Validator.validateString('Account Number', 'bankAccountNumeric', this.model.directDeposit.bankAccountNumeric, true, 50);
        await Appl.Validator.validateString('Confirm Account Number', 'confirmbankAccountNumeric', this.model.directDeposit.confirmbankAccountNumeric, true, 50);
        if (this.model.directDeposit.bankAccountNumeric !== this.model.directDeposit.confirmbankAccountNumeric) {
            await Appl.Validator.setError('confirmbankAccountNumeric', 'Account numbers do not match.');
        }
        if (this.model.directDeposit.lovAccountTypeId === 0) {
            await Appl.Validator.setError('lovAccountTypeId', 'Please select a type of deposit account.');
        }
        if (this.model.directDeposit.bankRoutingNumeric?.length !== 9) {
            await Appl.Validator.setError('bankRoutingNumeric', 'Routing number should be 9 digits.');
        }
        if (!Appl.Validator.isValid()) { return false; }
        const result = await this._service?.createOrUpdateDirectDepositAsync(this.model.directDeposit);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.DirectDepositAuthorization, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.DirectDeposit}/${Steps.DirectDepositMemberDeclaration}`)
        return true;
    }

    @action
    public async onDirectDepositMemberDeclarationStepCompletedAsync(): Promise<boolean> {
        this.model.directDeposit.submissionId = this.submissionId;
        this.directDepositModelCompleted = true;
        await this.setStepStatus(Steps.DirectDepositMemberDeclaration, StepStatus.Completed);
        if (this.navigatedFromReviewScreen) {
            await this.navigateToReviewStepAsync();
        } else {
            await this.navigateToStepAsync(`${Steps.TemporaryAnnuity}`);
        }
        return true;
    }

    @action public async onTemporaryAnnuitySelectionStepAsync(): Promise<boolean> {
        const result = await this._service?.createOrUpdateTemporaryAnnuityAsync(this.model.temporaryAnnuity);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.TemporaryAnnuity, StepStatus.Completed);
        if (this.navigatedFromReviewScreen) {
            await this.navigateToReviewStepAsync();
        } else {
            await this.navigateToStepAsync(`${Steps.ServiceRetirementSignOff}`);
        }
        return true;
    }


    @action
    public async onServiceRetirementSignOffStepCompletedAsync(): Promise<boolean> {
        const result = await this._service?.createOrUpdateServiceRetirementSignOffAsync(this.submissionId!, this.model.signOffModels);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return false;
        }
        await this.setStepStatus(Steps.ServiceRetirementSignOff, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.ReviewApplication}`)
        return true;
    }

    @action onSignOffCheckboxChange = (e: any, model?: object, fieldId?: string) => {
        (model as any)[fieldId!] = e.target.checked;
        if (e.target.checked) {
            this.signOffCheckedItemsCount++;
        } else {
            this.signOffCheckedItemsCount--;
        }
    };

    @action public async onReviewApplicationStepChangeAsync(): Promise<boolean> {
        await this.setStepStatus(Steps.ReviewApplication, StepStatus.Completed);
        await this.navigateToStepAsync(`${Steps.UploadDocuments}`);
        return true;
    }

    @action public async updateSelfReviewStatusAsync(status: string): Promise<boolean> {
        if (!this.model.personalInformation.retirementEffDate) {
            this.model.personalInformation.retirementEffDate = this.submission?.retirementDate as any;
        }
        if (this.model.personalInformation.retirementEffDate) {
            let dateCheck = await this.doRetirementDateValidationAsync(this.model.personalInformation.retirementEffDate!, true);
            if (dateCheck !== "") {
                Appl.ConfirmBox.show(dateCheck, "Retirement Date Update Required", "", "Update Retirement Date", "Cancel",
                    async (e: any) => await this.navigateFromReviewStepAsync(e, `/ora/application/${this.memberId}/${Steps.SelectDate}`), undefined, true)
                return false;
            }
        }
        let allDataEntryIsValid = await this.onValidateBeneficiaryStepAsync(true);
        if (!allDataEntryIsValid) {
            Appl.ConfirmBox.show("Your recent changes have affected who can be designated as a beneficiary for your retirement benefit.<br/><br/>To proceed, you’ll need to review and update your <b>Retirement Benefit Beneficiary</b> designations.", "Beneficiary Update Required", "", "Update Beneficiaries", "Cancel",
                async (e: any) => await this.navigateFromReviewStepAsync(e, `/ora/application/${this.memberId}/${Steps.BeneficiaryDesignation}/${Steps.BeneficiaryBenefit}`), undefined, true)
            return false;
        } else {
            await this._service?.updateSelfReviewStatusAsync(this.submissionId!, status);
            await this.setStepStatus(Steps.ReviewApplication, StepStatus.Completed);
            await this.navigateToStepAsync(`${Steps.ReviewApplication}`);
            return true;
        }
    }

    @action public async showUploadDialogAsync(doc?: DocumentsModel): Promise<void> {
        this.uploadDialogVisible = true;
        this.documentUploadCompleted = false;
        if (doc) {
            this.activeDocument = { docTypeId: doc.docTypeId }
            this.activeDocument.docType = this.requiredDocuments.find(o => o.docType === doc.docType)?.docType
        } else {
            this.activeDocument = { docTypeId: 1000 }
            this.activeDocument.docType = "";
        }
    }

    @action public async hideUploadDialogAsync(): Promise<void> {
        this.uploadDialogVisible = false;
        this.documentUploadCompleted = false;
        this.activeDocument = { docTypeId: 1000 }
        this.activeDocument.docType = '';//this.model.documentList?.find(o => o.key === 1000)?.value;
    }

    @action public async onRemoveDocumentAsync(docId: number): Promise<void> {
        const result = await this._service?.deleteDocumentsAsync(docId);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
        } else {
            let submittedDocResult = await this._service?.getSubmittedDocumentsAsync(this.submissionId!);
            if (submittedDocResult.success) {
                runInAction(() => {
                    this.model.uploadedDocuments = [];
                    this.model.uploadedDocuments = submittedDocResult.data;
                    this.setRequiredDocuments();
                });
            }
        }
    }

    @action public async downloadDocumentAsync(id: number, fileName: string): Promise<void> {
        const result = await this._service?.downloadAsync(`/retirement/Retiree/download/${this.submissionId}/${id}`);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
        } else {
            Helper.downloadFile(result.data, fileName, '');
        }
    }

    @action public setRequiredDocuments(): void {
        this.requiredDocuments = [];
        this.documentUploadCompleted = false;
        let memberDocumentType = `${SupportDocs.BirthCertificateMember} - ${this.model.personalInformation.firstName} ${this.model.personalInformation.lastName}`;
        let memberBirthCert = this.model.uploadedDocuments.find(o => o.docType === memberDocumentType);
        this.requiredDocuments.push({ docId: memberBirthCert?.docId, docTypeId: 0, docType: memberDocumentType, docFilename: memberBirthCert?.docFilename, isNew: memberBirthCert === undefined });
        if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Married) {
            let marriageCert = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.MarriageCertificate);
            this.requiredDocuments.push({ docId: marriageCert?.docId, docTypeId: 1, docType: SupportDocs.MarriageCertificate, docFilename: marriageCert?.docFilename, isNew: marriageCert === undefined });
            let spouseBirthCert = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.BirthCertificateSpouse);
            this.requiredDocuments.push({ docId: spouseBirthCert?.docId, docTypeId: 2, docType: SupportDocs.BirthCertificateSpouse, docFilename: spouseBirthCert?.docFilename, isNew: spouseBirthCert === undefined });
        } else if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.RegistedDomesticPartner) {
            //require certificate of domestic partnership and partner's birth certificate
            let partnerbirthCert = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.BirthCertificatePartner);
            this.requiredDocuments.push({ docId: partnerbirthCert?.docId, docTypeId: 3, docType: SupportDocs.BirthCertificatePartner, docFilename: partnerbirthCert?.docFilename, isNew: partnerbirthCert === undefined });
            let domasticPartner = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.CertificateOfDomesticPartnership);
            this.requiredDocuments.push({ docId: domasticPartner?.docId, docTypeId: 4, docType: SupportDocs.CertificateOfDomesticPartnership, docFilename: domasticPartner?.docFilename, isNew: domasticPartner === undefined });
        } else if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Divorced) {

        } else if (this.model.personalInformation.lovPersonalStatusId === PersonalStatus.Widowed) {
            //require spouse's death certificate
            let dthCert = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.DeathCertificateSpouse);
            this.requiredDocuments.push({ docId: dthCert?.docId, docTypeId: 6, docType: SupportDocs.DeathCertificateSpouse, docFilename: dthCert?.docFilename, isNew: dthCert === undefined });
        }

        if (this.model.directDeposit.lovAccountTypeId === AccountType.CheckingAccount) {
            let voidCheck = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.DirectDepositAuthorizationVoidedCheck);
            this.requiredDocuments.push({ docId: voidCheck?.docId, docTypeId: 7, docType: SupportDocs.DirectDepositAuthorizationVoidedCheck, docFilename: voidCheck?.docFilename, isNew: voidCheck === undefined });
        } else if (this.model.directDeposit.lovAccountTypeId === AccountType.SavingsAccount) {
            let bankLetter = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.CertifiedBankLetterOrSavingsAccountStatement);
            this.requiredDocuments.push({ docId: bankLetter?.docId, docTypeId: 8, docType: SupportDocs.CertifiedBankLetterOrSavingsAccountStatement, docFilename: bankLetter?.docFilename, isNew: bankLetter === undefined });
        }
        if (this.model.temporaryAnnuity.tempAnnuitySelection === "A") {//Temporary Annuity
            let tempAnuity = this.model.uploadedDocuments.find(o => o.docType === SupportDocs.TemporaryAnnuitiesEstimateFromSocialSecurityOffice);
            this.requiredDocuments.push({ docId: tempAnuity?.docId, docTypeId: 9, docType: SupportDocs.TemporaryAnnuitiesEstimateFromSocialSecurityOffice, docFilename: tempAnuity?.docFilename, isNew: tempAnuity === undefined });
        }
        let benes = this.model.beneficiaryDesignation?.beneficiaries;//?.filter(o => o.lovBeneRelationshipId !== BeneficiaryRelation.NoEligibleBeneficiary);
        let spouseBirtCertExists = this.requiredDocuments.find(o => o.docType == SupportDocs.BirthCertificateSpouse) !== undefined;
        let rdpBirtCertExists = this.requiredDocuments.find(o => o.docType == SupportDocs.BirthCertificatePartner) !== undefined;
        if (benes && benes.length > 0) {
            let num = 9;
            for (let i = 0; i < benes.length; i++) {
                let ben = benes[i];
                if (ben.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg) {
                    if (ben.lovBeneRelationshipId === BeneficiaryRelation.Spouse && spouseBirtCertExists) {
                        continue;
                    }
                    if (ben.lovBeneRelationshipId === BeneficiaryRelation.RegisteredDomesticPartner && rdpBirtCertExists) {
                        continue;
                    }
                    let docName = `${SupportDocs.BeneficiariesBirthCertificates}${i + 1}`;
                    let benUploaded = this.model.uploadedDocuments.find(o => o.docType === docName);
                    this.requiredDocuments.push({ docId: benUploaded?.docId, docTypeId: num + 1, docType: docName, docFilename: benUploaded?.docFilename, isNew: benUploaded === undefined });
                }
            }
        }
        let otherDocs = this.model.uploadedDocuments.filter(o => o.docTypeId === 1000);
        this.requiredDocuments.push(...otherDocs);
    }

    @action public async onUploadDocumentsAsync(): Promise<void> {
        if (!this.model.uploadedDocuments) {
            this.model.uploadedDocuments = [];
        }
        if (!this.activeDocument.file) {
            Appl.MessageBox.show('Please choose a file.', 'Outstanding Documents', '', 'Okay', true);
            return;
        }
        this.model.uploadedDocuments.push(this.activeDocument);
        var docToUpload = this.model.uploadedDocuments.find(o => o.isNew);
        if (docToUpload) {
            this.documentUploading = true;
            let result = await this._service?.uploadAsync(`/retirement/Retiree/createOrUpdateRequiredDocuments/${this.submissionId}/${docToUpload.docTypeId}/${docToUpload.docType}`, this.model.uploadedDocuments);
            if (!result.success) {
                this.documentUploading = false;
                this.model.uploadedDocuments = [];
                let pb = Appl.Error.getValidationError(result.error);
                if (pb.pointer === 'fileName') {
                    Appl.MessageBox.show(pb.detail, "Duplicate File Name Detected", '', "Okay", true);
                } else if (pb.pointer === 'fileFormat') {
                    Appl.MessageBox.show(pb.detail, "File Type Not Supported", '', "Okay", true);
                } else {
                    Appl.Error.handle(result.error, true);
                }
                return;
            } else {
                result = await this._service?.getSubmittedDocumentsAsync(this.submissionId!);
                if (result.success) {
                    runInAction(() => {
                        this.model.uploadedDocuments = [];
                        this.model.uploadedDocuments = result.data;
                        this.documentUploadCompleted = true;
                        this.documentUploading = false;
                        this.setRequiredDocuments();
                        this.hideUploadDialogAsync();
                    });
                }
            }
        }
    }
    @action public async onSubmitRequiredDocumentsAsync(): Promise<void> {

        if (this.model.uploadedDocuments.length < this.requiredDocuments.length) {
            Appl.ConfirmBox.show(
                ` <p class="text-[15px] text-gray-800">
                      You can proceed to the next step to submit your online retirement application. 
                      However, please note that your application will not be fully processed until all required documentation is submitted.
                    </p>
                    <br/>
                    <p class="text-[15px] text-gray-800">
                      <span class="font-bold"> Important: </span> 
                      Moving to the next step does not guarantee that your application will be processed until all required documentation is received. 
                      Your retirement application will remain incomplete until everything is submitted.
                    </p>
                    <br/>
                    <p class="text-[15px] text-gray-800 mt-2">
                      If you wish to proceed, click "Save & Continue".
                    </p>
                    `,
                "Outstanding Documents",
                "",
                "Save & Continue",
                "Cancel",
                async () => {
                    await this.setStepStatus(Steps.UploadDocuments, StepStatus.Completed);
                    await this.navigateToStepAsync(`${Steps.SubmitApplication}`);
                },
                undefined, true
            )
        } else {
            await this.setStepStatus(Steps.UploadDocuments, StepStatus.Completed);
            await this.navigateToStepAsync(`${Steps.SubmitApplication}`);
        }
    };

    @action
    public async sendApplicationForSignAsync(): Promise<boolean> {
        Appl.Validator.init();
        await Appl.Validator.validateString('Full Name', 'fullName', this.model.personalInformation.fullName, true, 200);
        if (!Appl.Validator.isValid()) { return false; }
        const docuSignResult = await this._service?.sendDocumentForDocuSignAsync(this.submissionId!, this.model.personalInformation.fullName!);
        if (docuSignResult.success) {
            runInAction(() => {
                window.location.href = docuSignResult.data;
            });
        } else {
            Appl.Error.handle(docuSignResult.error, true);
        }
        return true;
    }

    @action
    public async submitApplicationAsync(submissionId: string, memberId: string, envolopeId: string, documentId: string, status: string): Promise<boolean> {
        this.memberId = memberId;
        this.docuSignMessage = '';
        this.docuSignEventCode = status;
        if (this.docuSignEventCode === "signing_complete") {
            const result = await this._service?.submitApplication(submissionId, envolopeId, documentId, this.docuSignEventCode);
            if (!result.success) {
                Appl.Error.handle(result.error, true);
                return false;
            }
            await this.setStepStatus(Steps.SubmitApplication, StepStatus.Completed);
            this.submission!.lovSubmissionStatusId = SubmissionStatus.Submitted;
            await this.navigateToStepAsync(`${Steps.SubmitApplication}`)
        } else {
            if (this.docuSignEventCode === "cancel" || this.docuSignEventCode === "decline") {
                await this.navigateToStepAsync(`${Steps.SubmitApplication}`)
            }
            else if (this.docuSignEventCode === "session_timeout") {
                this.docuSignMessageTitle = "You've Been Logged Out Due to Inactivity";
                this.docuSignMessage = `To help protect your personal information, your session has ended after a period of inactivity.<br/><br/>Don't worry-your progress has been saved.<br/>When you're ready, you can log back in to continue your retirement application.<br/><br/>`;
                this.navigate(`/logout?mode=session&from=docusign`)
            }
            else {
                this.docuSignMessageTitle = "Unhandled Error from Docusign";
                this.docuSignMessage = status;
            }
        }
        return true;
    }

    public async getSubmissionStatus(memberId: string): Promise<void> {
        const result = await this._service?.getSubmissionByMemberIdAsync(memberId);
        if (!result.success) {
            Appl.Error.handle(result.error, true);
            return;
        } else {
            runInAction(() => {
                this.submission = result.data
            });
        }
    }

    @action public async download6105bSignedDoc(submissionId: number): Promise<void> {
        const result = await this._service?.downloadAsync(`/retirement/Retiree/getSignedDocument/${submissionId}/6105b`);
        if (result.success) {
            Helper.downloadFile(result.data, `6105b-${submissionId}.pdf`, 'pdf');
        } else {
            Appl.Error.handle(result.error, true);
        }
    }

    @action public async download6105bDoc(submissionId: number): Promise<void> {
        const result = await this._service?.downloadAsync(`/retirement/Retiree/getAppContentAsPdf/${submissionId}`);
        if (result.success) {
            this.afrFile = result.data
        } else {
            Appl.Error.handle(result.error, true);
        }
    }

    @action public async searchAddress(filter?: string): Promise<void> {
        const result = await this.lovService.getListOfValuesAsync('address-lookup', filter);
        runInAction(async () => {
            if (result.success) {
                this.addressListItems = result.data;
            } else {
                Appl.Error.handle(result.error, true);
            }
        });
    }

    @action public async navigateToStepAsync(url: string): Promise<boolean> {
        this.navigate(`/ora/application/${this.memberId}/${url}`);
        return true;
    }

    @action public async navigateToReviewStepAsync(): Promise<void> {
        this.navigatedFromReviewScreen = false;
        this.navigate(`/ora/application/${this.memberId}/print/${this.submissionId}`);
    }

    @action public async navigateFromReviewStepAsync(e: any, url: string): Promise<void> {
        if (e) {
            e.preventDefault();
        }
        Appl.MessageBox.hide();
        this.navigatedFromReviewScreen = true;
        this.retirementDateConfirmed = false;
        this.personalInfoEditable = true;
        this.navigate(url);
    }
}